import * as PIXI from "pixi.js";
import anime from "animejs";
import { symbolData } from "../../../SymbolData";
import { formatToCustomLocale } from "../../../utilities";
import { Settings } from "../../../settings";
import {
  COIN_SYMBOL_IDS,
  SPIN_FILLER_SYMBOL_IDS,
  ROYAL_SYMBOL_ID,
  isCoinSymbol,
  isJackpotSymbol,
  getSymbolZIndex,
} from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import SpineAnimationData from "../../../utils/SpineAnimationData";
import { setSpinComplete, incrementWindupCount } from "../../../State/actions";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import store from "../../../State/Store/store";
import { isTurboLike, isSuperTurbo } from "../../../utils/speedHelpers";
import { COIN_JACKPOT_SYMBOL_ID } from "../../../SymbolData";
import { createCoinLabel, setCoinLabel } from "../../HoldAndWin/coinLabel";
import { cosmeticCoinValue, coinValueAt, coinValueBet } from "../../Shared/coinValues";
import {
  formatCoinValue,
  warmCoinValueLabels,
  COIN_LABEL_MAX_WIDTH_RATIO,
} from "../../Shared/coinValueLabel";
import SpineTextureExtractor from "../../../utils/SpineTextureExtractor";

// Dynamic sprite count formula: 1 top buffer + N visible + N landing zone + 1 bottom buffer
const SPRITE_COUNT = Settings.SYMBOLS_VISIBLE * 2 + 2;
const VISIBLE_SYMBOL_COUNT = Settings.SYMBOLS_VISIBLE;
// Visible symbols start after top buffer (index 1) + landing zone (N symbols)
const VISIBLE_SYMBOL_START_INDEX = 1 + Settings.SYMBOLS_VISIBLE;
// Stop animation moves sprites down by the landing zone distance (N symbol heights)
const STOP_MOVE_DISTANCE_SYMBOLS = Settings.SYMBOLS_VISIBLE;

// The spin-blur pool. Derived in SymbolData from what the GDD and the BE
// doc allow to LAND — see SPIN_FILLER_SYMBOL_IDS there for the per-symbol
// reasoning. Previously computed here from a hardcoded 10-25 exclusion
// carried over from Zeus, whose comment described orbs and jackpots that
// do not exist in this game.
const AVAILABLE_SYMBOL_IDS = SPIN_FILLER_SYMBOL_IDS;

// Safari-specific precision factor (Safari doesn't need as much precision)
const PRECISION_FACTOR = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent,
)
  ? 10000
  : 100000;

// ── Where the number sits: bone `value` in c.json, and NOTHING else ──
//
// The static text has to land exactly where the live spine's slot puts it, or
// the number jumps at the handover between the flat cell and the animated one.
// So this is the bone's own y, in skeleton units, converted the same way the
// Golden Mystery's flat cell converts it (see COIN_VALUE_OFFSET_Y and the
// `!atBone` branch of buildCellNumber — one formula, two callers):
//
//   y = -COIN_VALUE_Y_OFFSET * (symbolHeight / Settings.SYMBOL_HEIGHT)
//
// It used to be `82.67 / 338` — a FRACTION of symbolHeight, ≈0.2446, read off
// an OLD export whose skeleton was 338 tall with `value` at y=-82.67. The rig
// has since been re-exported at 392.6 with `value` at y=0.13, so that constant
// put the number about a quarter of a symbol height too low. Negated for PIXI,
// which is Y-down.
const COIN_VALUE_Y_OFFSET = 0.13;

// Main game reel animation controller - handles wind-up, spin, stop animations with sprite wrapping
class ReelControllerV2 {
  constructor(
    dispatch,
    spinningContainer,
    spriteSheet,
    spriteSheetBlurred,
    // multiplierSpriteSheet, // TODO: Add multiplier support later
    reels,
    reelIndex,
    symbolWidth,
    symbolHeight,
    onReelStop = null, // Callback when reel stops (for landing animations)
    autoPlaySpeed = "auto", // Current autoplay speed mode
    bet = 1, // Current bet value for coin display
    reelXOffset = 0, // This reel's column x, carried per-sprite (see below)
  ) {
    this.dispatch = dispatch;
    this.spinningContainer = spinningContainer;
    // Reel-level vertical offset — formerly spinningContainer.y. Held as a
    // scalar and pushed onto each sprite instead, so the reel's symbols no
    // longer need a private moving container to ride on. Only the wind-up
    // tween drives this; the spin loop itself has always moved sprites
    // individually.
    this.reelYOffset = 0;
    this._reelYTweenTarget = { y: 0 };
    // Every reel's sprites live in ONE shared container so they can sort
    // against each other by z-index, so the column offset that used to come
    // from a per-reel parent is stamped on each sprite instead.
    this.reelXOffset = reelXOffset;
    this.reelIndex = reelIndex;
    this.spritesheet = spriteSheet;
    this.spritesheetBlurred = FEATURES_CONFIG.SPIN.BLURRED_TEXTURES_ENABLED
      ? spriteSheetBlurred
      : null;
    // symbolId -> { normal, blurred }, resolved ONCE. See _buildTextureTable.
    this._textureTable = this._buildTextureTable();
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reels = reels || []; // Store API landing data (array of symbol IDs)
    this.hasPendingLandingSymbols = false; // Flag to indicate API data is ready
    this.onReelStop = onReelStop; // Callback for landing animations
    this.onReelLanded = null; // Callback when reel has fully landed (bounce complete, after onReelStop)
    // Injected by Reel.jsx from useReelLandingAnimations. See _completeSpin.
    this.waitForBoardSettle = null;
    this.onLandingPeak = null; // Callback when reel hits overshoot trough (lowest visual point, before bounce-back)
    this.autoPlaySpeed = autoPlaySpeed; // Current speed setting
    this.lockedSpinSpeed = null; // Locked speed for current spin (prevents mid-spin changes)
    this.bet = bet || 1; // Store bet value for coin calculations

    // Animation state
    this.isPlaying = false;
    this.hasStopped = false;
    this.isStopping = false;
    this._wasManualStopped = false; // True if this reel was force-stopped by manual stop
    this.isTensionMode = false;
    this.isLastNonTensionReel = false; // True when this reel is the last to stop before tension reels begin
    this.hasTensionThisSpin = false; // True when tension is active this spin (set by context, used by landing hook)
    this.tickerCallback = null;
    this.spinningSprites = [];
    this._spinElapsed = 0; // Accumulated spin time (ms) — advances only when ticker runs
    this.normalizedSpinSpeed = 0;
    this.lastCalculatedPosition = 0;
    this.pendingLandingSymbolId = null; // Deprecated: use this.reels array instead
    this.isBlurred = false;
    this.visibleSprites = [];
    this.nativeDimensions = null;
    this.hasInitializedTextures = false;
    this.stopTweenActive = false;
    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = symbolHeight;
    this._targetSymbolHeight = symbolHeight;
    this.activeStopAnimation = null;
    this.stopAnimationProgress = 0;
    this.isWindingUp = false;
    this.windUpAnimation = null;
    // Which stop was requested while the wind-up was still running, if any.
    // "normal" | "turbo" | "tension" | null. See _deferStopDuringWindUp.
    this._pendingStopKind = null;
    this.speedMultiplier = 1.0;
    this.hasWinThisSpin = false; // Track if this spin has a win (affects sound playback)
    this.isHoldAndWinTrigger = false; // Track if this spin triggers Hold & Win (6+ coins)
    this.isExpandingWildActive = false; // Lock flag to prevent texture updates during expanding wild animation

    // Premium feel: acceleration, deceleration, velocity-based blur
    this.accelerationMultiplier = 1.0; // Ramps 0→1 during spin start
    this.currentVelocity = 0; // Effective velocity as fraction of max (0-1), used for blur
    this.isDecelerating = false; // True during phase 1 of 2-phase stop

    // Tension deceleration state (smooth slowdown when entering tension mode)
    this._tensionDecelerating = false; // True while reel is decelerating for tension
    this._tensionDecelElapsed = 0; // Accumulated tension deceleration time (ms)
    this._tensionDecelerationDuration = 0; // Computed at spinTension(): cycleDuration × DECELERATION_RATIO
    this._tensionSpeedMultiplier = 1.0; // Current speed multiplier (1.0 = full, ramps down to TARGET_SPEED)

    // Stop animation timing (accumulated frame time)
    this._stopElapsed = 0; // Accumulated stop animation time (ms)

    // Pause/resume: handled automatically by PIXI.Ticker.shared
    // When AppActivityContext sets ticker.speed = 0, deltaMS = 0
    // → all accumulated timers freeze in place → zero restoration code

    // Get symbol data from spritesheet
    this.reelSymbolsData =
      spriteSheet?.data?.frames || spriteSheet?.textures || null;

    // Internal caches for performance optimization
    this._speedConfigCache = null;
    this._lastLockedSpeed = null;
    this._lastAutoPlaySpeed = null;
    this._nativeDimensionsCache = new Map(); // Cache per symbol name
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;

    this._createSpinningSprites();
    this.spinningContainer.roundPixels = true;
  }

  // Picks a random symbol from available symbols (O(1) direct random)
  _pickRandomSymbolId() {
    if (!AVAILABLE_SYMBOL_IDS.length) {
      return 1; // fallback
    }
    return AVAILABLE_SYMBOL_IDS[
      Math.floor(Math.random() * AVAILABLE_SYMBOL_IDS.length)
    ];
  }

  // Calculates display value for coin symbols (coinValue * bet) with locale formatting
  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Use free rounds bet when active, otherwise use regular bet
      const state = store.getState();
      const freeRoundsActive = state?.freeRounds?.status === "active";
      const freeRoundsBet = state?.freeRounds?.betValue || null;
      const bet = freeRoundsActive && freeRoundsBet ? freeRoundsBet : this.bet;

      const displayValue = coinValue * bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  // Receives new reel data from API - used for landing symbols when reel stops
  updateReels(newReels) {
    if (newReels && Array.isArray(newReels)) {
      this.reels = newReels;
      this.hasPendingLandingSymbols = true;
    }
  }

  // Updates bet value - coin texts will refresh naturally during spin when symbols wrap
  updateBet(newBet) {
    this.bet = newBet || 1;
    this._warmCoinLabels();
    // Note: Coin texts update automatically when symbols wrap during spin via _setTexture()
    // This prevents immediate texture updates and maintains smooth gameplay
  }

  // Refresh all visible coin value texts (used when bet changes during free rounds)
  _refreshVisibleCoinTexts() {
    if (!this.spinningSprites || this.spinningSprites.length === 0) return;

    // Refresh ALL spinning sprites (not just visibleSprites which may not be set yet)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.valueText || !spriteData.currentSymbolId)
        continue;

      const symbolInfo = symbolData[spriteData.currentSymbolId];
      if (!symbolInfo || !symbolInfo.value) continue;

      // Skip jackpot symbols — they don't display text values
      if (isJackpotSymbol(spriteData.currentSymbolId)) continue;

      // Recalculate coin display value with new bet
      const displayValue = this._getCoinDisplayValue(symbolInfo.value);
      if (displayValue && displayValue !== "") {
        spriteData.valueText.text = displayValue;
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.text = displayValue;
        }
      }
    }
  }

  // Updates texture for spinning sprites
  updateSymbolTexture(symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo || this.spinningSprites.length === 0) return;

    const visibleSpriteIndex = this.spinningSprites.length - 1;
    this._setSpriteTexture(this.spinningSprites[visibleSpriteIndex], symbolId);
    this.symbolId = symbolId;
  }

  // Creates spinning sprites with wrapping
  _createSpinningSprites() {
    if (!this.spinningContainer) return;

    for (let i = 0; i < SPRITE_COUNT; i++) {
      const symbolContainer = new PIXI.Container();
      symbolContainer.x = this.reelXOffset;
      symbolContainer.y = i * this.symbolHeight;
      symbolContainer.roundPixels = true;

      const normalSprite = new PIXI.Sprite();
      normalSprite.anchor.set(0.5);
      normalSprite.name = "spinning_symbol_texture";
      normalSprite.alpha = 1;
      normalSprite.roundPixels = true;
      symbolContainer.addChild(normalSprite);

      const blurredSprite = new PIXI.Sprite();
      blurredSprite.anchor.set(0.5);
      blurredSprite.name = "spinning_symbol_texture_blurred";
      blurredSprite.alpha = 0;
      blurredSprite.roundPixels = true;
      symbolContainer.addChild(blurredSprite);

      let valueText = null;
      let valueTextBlurred = null;
      // Cleopatra's Fire: the coin number is the Hold & Win label sprite
      // (coinLabel — one cached texture per value, never rounded). The Zeus
      // BitmapText below needs a bitmap font this game does not load.
      if (FEATURES_CONFIG.SPIN.COIN_VALUES_ON_REELS && COIN_SYMBOL_IDS.length > 0) {
        valueText = createCoinLabel();
        valueText.text = ""; // the reel's checks read `.text`
        valueText.name = "symbol_value";
        valueText.zIndex = 1;
        symbolContainer.addChild(valueText);
      }
      // NOT USED — the Zeus BitmapText, which needs fonts this game does not load:
      //   const fontSize =
      //     this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
      //   valueText = new PIXI.BitmapText("", {
      //     fontName: "font_secondary_scaled",
      //     fontSize: fontSize,
      //   });
      //   valueText.anchor.set(0.5);
      //   valueText.name = "symbol_value";
      //   valueText.zIndex = 1;
      //   valueText.alpha = 0;
      //   valueText.roundPixels = true;
      //   symbolContainer.addChild(valueText);
      //
      //   if (FEATURES_CONFIG.SPIN.BLURRED_TEXTURES_ENABLED && PIXI.BitmapFont.available["font_secondary_blurred"]) {
      //     valueTextBlurred = new PIXI.BitmapText("", {
      //       fontName: "font_secondary_blurred",
      //       fontSize: fontSize,
      //     });
      //     valueTextBlurred.anchor.set(0.5);
      //     valueTextBlurred.name = "symbol_value_blurred";
      //     valueTextBlurred.zIndex = 1;
      //     valueTextBlurred.alpha = 0;
      //     valueTextBlurred.roundPixels = true;
      //     symbolContainer.addChild(valueTextBlurred);
      //   }

      this.spinningSprites.push({
        container: symbolContainer,
        normalSprite,
        blurredSprite,
        valueText,
        valueTextBlurred,
        currentSymbolId: null,
      });

      this.spinningContainer.addChild(symbolContainer);
    }

    this._updateSpinningSprites();
    this._hideBufferSprites();
    this.hasInitializedTextures = true;
    const visibleStart = VISIBLE_SYMBOL_START_INDEX;
    const visibleEnd = visibleStart + VISIBLE_SYMBOL_COUNT;
    this.visibleSprites = this.spinningSprites.slice(visibleStart, visibleEnd);
  }

  /**
   * Darken (or restore) this reel's own spinning sprites with a tint — the
   * scatter anticipation's "reels waiting their turn" dim. No overlay: the
   * reel's own sprites, symbol and coin value alike. 0x666666 reads the same
   * as the old 0.6 black DimReelOverlay.
   *
   * Applied to the sprites that exist now; it is set per spin, so a resize
   * rebuilding the sprites mid-spin only loses it until the next call.
   */
  setDimmed(dimmed, tint = 0x666666) {
    const value = dimmed ? tint : 0xffffff;
    for (const spriteData of this.spinningSprites ?? []) {
      if (spriteData.normalSprite) spriteData.normalSprite.tint = value;
      if (spriteData.blurredSprite) spriteData.blurredSprite.tint = value;
      if (spriteData.valueText) spriteData.valueText.tint = value;
      if (spriteData.valueTextBlurred) spriteData.valueTextBlurred.tint = value;
    }
  }

  // Calculates symbol scale with per-category overrides.
  // Single lookup in SYMBOL_SCALE_OVERRIDES (with DEFAULT fallback) replaces
  // the old padding + override + feature-skip system.
  _calculateSymbolScale(symbolName, containerWidth, containerHeight, symbolId) {
    if (!containerHeight) return 1;

    // Look up symbolPay for per-category scale
    const info = symbolId != null ? symbolData[symbolId] : null;
    const symbolPay = info?.symbolPay || '';
    const categoryScale = Settings.SYMBOL_SCALE_OVERRIDES?.[symbolPay]
      ?? Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT
      ?? 1;

    // Check cache
    const cacheKey = `scale_${containerWidth}_${containerHeight}_${symbolPay}`;
    if (this._nativeDimensionsCache.has(cacheKey)) {
      return this._nativeDimensionsCache.get(cacheKey);
    }

    const scale = (containerHeight / Settings.SYMBOL_HEIGHT) * categoryScale;

    // Cache result
    this._nativeDimensionsCache.set(cacheKey, scale);

    return scale;
  }

  /**
   * The scale a COMPOSITE is drawn at.
   *
   * A composite — the settled gamble scatter's open window, a Golden cell — is
   * captured off its spine at native size, so one texture pixel is one
   * skeleton unit. The live spine it stands in for is scaled by
   * `symbolHeight / Settings.SYMBOL_HEIGHT` and nothing else, so the sprite
   * must be too, and the two are then the same picture at the same size.
   *
   * No category multiplier and no symbol lookup: there is no symbolData id for
   * a composite, and the id the CELL still carries describes the closed door,
   * not what is drawn on it.
   */
  _applyCompositeScale(spriteData) {
    if (!this.symbolHeight) return;
    const scale = this.symbolHeight / Settings.SYMBOL_HEIGHT;
    if (spriteData.normalSprite) spriteData.normalSprite.scale.set(scale);
    if (spriteData.blurredSprite) spriteData.blurredSprite.scale.set(scale);
  }

  // Sets sprite textures (normal/blurred) and coin value text
  /**
   * Render priority for a static texture, matching the spine overlay's
   * hierarchy so both layers agree: M > D > C > jackpots > coin > scatter >
   * wild > pays. Matters most in turbo and super turbo, where landing spines
   * are skipped entirely and the texture IS the visible symbol.
   *
   * Guarded on change: writing zIndex flags the shared container sortDirty,
   * and this runs for every sprite as symbols recycle during a spin.
   */
  _applySpriteZIndex(spriteData, symbolId) {
    const container = spriteData?.container;
    if (!container || container.destroyed) return;
    const zIndex = getSymbolZIndex(symbolId);
    if (container.zIndex !== zIndex) container.zIndex = zIndex;
  }

  /**
   * Resolve every symbol's textures ONCE, at construction.
   *
   * ── What this is NOT ──
   *
   * It is not a texture pool. The spritesheet already IS one: its textures are
   * uploaded to the GPU at load and we only ever point sprites at them. Nothing
   * here is rendered, generated or cached in the sense of being produced.
   *
   * ── What it removes ──
   *
   * The lookup itself. `_setSpriteTexture` ran this on every recycled cell:
   *
   *     this.spritesheet.textures[`${textureName}.png`]
   *     this.spritesheetBlurred.textures[`${textureName}_blurred.png`]
   *
   * Two template strings BUILT and two hashes computed, per sprite, per wrap.
   * At turbo a cell crosses in 36ms, so the strip advances more than half a
   * cell per frame and several sprites wrap per frame across five reels — a
   * steady stream of short-lived strings for an answer that can never change,
   * because the sheets are assigned once in this constructor and never
   * reassigned.
   *
   * The lookup is now one Map.get on a number.
   *
   * Falls back to the old path per call if a symbol is missing here, so a sheet
   * that arrives incomplete degrades exactly as it did before rather than
   * showing nothing.
   *
   * @returns {Map<number, {normal: PIXI.Texture, blurred: PIXI.Texture}>}
   */
  _buildTextureTable() {
    const table = new Map();
    const sheet = this.spritesheet?.textures;
    if (!sheet) return table;

    for (const key of Object.keys(symbolData)) {
      const textureName = symbolData[key]?.texture;
      if (!textureName) continue;
      const normal = sheet[`${textureName}.png`];
      if (!normal) continue;
      // Mirrors the old fallback: no blurred sheet, or no blurred frame for
      // this symbol, means the normal texture is used for both.
      const blurred =
        this.spritesheetBlurred?.textures?.[`${textureName}_blurred.png`] ||
        normal;
      table.set(Number(key), { normal, blurred });
    }
    return table;
  }

  /**
   * The value the LANDING coin at `row` of this reel shows — the round's own
   * (round.coinValues, decided once in the adapter), so the picture and the
   * spine that plays on it agree. Null off the round: a cosmetic one is drawn.
   */
  _landingCoinValue(row) {
    return coinValueAt(store.getState().game?.currentRound, this.reelIndex, row);
  }

  /**
   * The skin the LANDING jackpot coin at `row` wears (round.jackpotCoinSkins,
   * PopiPlay RGS markJackpotCoins) — its picture shows the tier word like its
   * spine does. Null for any other cell.
   */
  _landingJackpotSkin(row) {
    return (
      store.getState().game?.currentRound?.jackpotCoinSkins?.[`${this.reelIndex}_${row}`] ??
      null
    );
  }

  /**
   * Pre-draw the coin labels this reel can show (the bet's seven cosmetic
   * values, at this reel's size and the spine's) — once per bet and size, so
   * nothing is rasterised while the strip moves. Cheap to call: keyed.
   */
  _warmCoinLabels() {
    if (!FEATURES_CONFIG.SPIN.COIN_VALUES_ON_REELS || !this.symbolHeight) return;
    const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
    const key = `${coinValueBet()}|${Math.round(fontSize)}`;
    if (this._coinWarmKey === key) return;
    this._coinWarmKey = key;
    warmCoinValueLabels(fontSize);
  }

  // _setSpriteTexture(spriteData, symbolId, coinValue = null) {
  _setSpriteTexture(spriteData, symbolId, coinValue = null, jackpotSkin = null) {
    // ── An override is cleared HERE, and nowhere else ──
    //
    // A settled reveal's cell carries a COMPOSITE picture with the same
    // identity it always had — see setSymbolTextureOnly. That picture must
    // survive the start of the next spin: the cell the player is looking at
    // travels up and out of view exactly as it is, frame and all. Taking it off
    // when the reels start strips the frame in mid-air.
    //
    // This method is the one moment a sprite legitimately becomes something
    // else: it is called when a sprite RECYCLES to the far end of the strip and
    // is dealt a new symbol. That is "out of the view field", and it is where
    // the composite goes.
    //
    // The override has to be part of the early-return test, not just cleared
    // after it. The guard exists because sprites recycle every frame and
    // repainting an unchanged cell is wasted work — but a recycled sprite dealt
    // the SAME id it already held would take that return and keep the
    // composite for ever, riding the strip as a framed symbol. Always in the
    // same slots, because the pool recycles in order, which is why it read as
    // "the random symbols are always the same".
    // if (spriteData.currentSymbolId === symbolId && !spriteData._staticOverride) {
    //   return;
    // }
    // A jackpot coin's tier picture is part of "the same": the same id in
    // another skin (or none) is repainted.
    // A LANDING coin's number is part of it too: a sprite that was already a
    // coin on the strip kept its random number, while the spine showed the
    // round's — picture and spine disagreed (Vladimir, 2026-09-30). A coin
    // passing on the strip (no value asked for) still keeps what it shows.
    if (
      spriteData.currentSymbolId === symbolId &&
      !spriteData._staticOverride &&
      (spriteData._jackpotSkin ?? null) === jackpotSkin &&
      (coinValue == null || spriteData._coinValue === coinValue)
    ) {
      return;
    }
    spriteData._staticOverride = null;
    spriteData._jackpotSkin = null;

    // Sprites recycle every frame, so a cell that once carried a gamble spin
    // count must not still be showing it as a different symbol. Cleared here
    // because this is the one choke point every symbol assignment flows

    // Ahead of every early return below, so a sprite that lands on an unknown
    // or textureless symbol still drops back to the default layer.
    this._applySpriteZIndex(spriteData, symbolId);

    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    spriteData.currentSymbolId = symbolId;
    const symbolName = symbolInfo.name;
    const textureName = symbolInfo.texture;
    const symbolValue = symbolInfo.value;

    // If texture is null or empty, set empty textures and skip processing
    if (!textureName || textureName === null) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    if (this.spritesheet && textureName) {
      // One Map.get on a number, instead of building two strings and hashing
      // them — on every recycled cell of every reel, every frame. The `||`
      // chains are the pre-table behaviour, kept as the fallback.
      const entry = this._textureTable?.get(symbolId);

      const texture =
        entry?.normal ||
        this.spritesheet.textures?.[`${textureName}.png`] ||
        PIXI.Texture.EMPTY;
      spriteData.normalSprite.texture = texture;
      spriteData.normalSprite.alpha = this.isBlurred ? 0 : 1;

      // ── A landing jackpot coin shows its tier word (MINI … GRAND) ──
      // The sheet's `cj` is the default skin — the bare coin — so the picture
      // under the spine was empty (Vladimir, 2026-09-30). Its skin's own
      // picture, from the same extraction (SpineTextureExtractor, cached).
      if (symbolId === COIN_JACKPOT_SYMBOL_ID && jackpotSkin) {
        const skinned = SpineTextureExtractor.getSkinTexture(symbolInfo.spine, jackpotSkin);
        if (skinned) {
          spriteData.normalSprite.texture = skinned;
          spriteData._jackpotSkin = jackpotSkin;
        }
      }

      if (this.spritesheetBlurred && spriteData.blurredSprite) {
        const blurredTexture =
          entry?.blurred ||
          this.spritesheetBlurred.textures?.[`${textureName}_blurred.png`] ||
          PIXI.Texture.EMPTY;
        spriteData.blurredSprite.texture = blurredTexture;
        spriteData.blurredSprite.alpha = this.isBlurred ? 1 : 0;
      }
    }

    if (symbolName) {
      const scale = this._calculateSymbolScale(
        symbolName,
        this.symbolWidth,
        this.symbolHeight,
        symbolId,
      );
      if (spriteData.normalSprite) {
        spriteData.normalSprite.scale.set(scale);
      }
      if (spriteData.blurredSprite) {
        // Blurred textures have same canvas size as normal (squash is baked into content).
        // Use same uniform scale so blurred sprites match normal sprite size.
        spriteData.blurredSprite.scale.set(scale);
      }
    }

    // ── Cleopatra's Fire: every plain coin shows a number ──
    // A landing coin its round value (`coinValue`), a coin passing on the
    // strip a cosmetic one. Never the jackpot coin (its tier word is its face).
    if (
      spriteData.valueText &&
      isCoinSymbol(symbolId) &&
      symbolId !== COIN_JACKPOT_SYMBOL_ID
    ) {
      const value = coinValue ?? cosmeticCoinValue();
      spriteData._coinValue = value; // what it shows — see the guard above
      const label = formatCoinValue(value);
      setCoinLabel(
        spriteData.valueText,
        label,
        this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER,
        // this.symbolWidth * 0.76,
        this.symbolHeight * COIN_LABEL_MAX_WIDTH_RATIO, // the spine's ratio
      );
      spriteData.valueText.text = label; // read by the reel's own checks
      spriteData.valueText.y = 0;
      return;
    }

    // Update coin value text — skip entirely when symbol has no value (most symbols during spin)
    // Also skip jackpot symbols (27-29) — they have a value for payout but display tier via spine skins, not text
    if (spriteData.valueText && symbolValue && !isJackpotSymbol(symbolId)) {
      const displayValue = this._getCoinDisplayValue(symbolValue);
      spriteData.valueText.text = displayValue;
      spriteData.valueText.alpha =
        displayValue !== "" ? (this.isBlurred ? 0 : 1) : 0;
      spriteData.valueText.y =
        -COIN_VALUE_Y_OFFSET * (this.symbolHeight / Settings.SYMBOL_HEIGHT);

      // Apply scaling logic if text has content
      if (displayValue !== "") {
        // Reset scale first
        spriteData.valueText.scale.set(1);

        // Force update to get accurate measurements
        spriteData.valueText.updateText();

        // Calculate available width (with padding)
        const coinWidth = this.symbolWidth;
        const paddingPercent = 0.12; // 12% padding on each side
        const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

        const actualTextWidth = spriteData.valueText.width;

        // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
        if (actualTextWidth > maxTextWidth) {
          const scale = maxTextWidth / actualTextWidth;
          spriteData.valueText.scale.set(scale);
        }
      }

      // Update blurred text with same value and scaling
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = displayValue;
        spriteData.valueTextBlurred.alpha =
          displayValue !== "" ? (this.isBlurred ? 1 : 0) : 0;
        spriteData.valueTextBlurred.y =
          -COIN_VALUE_Y_OFFSET * (this.symbolHeight / Settings.SYMBOL_HEIGHT);

        if (displayValue !== "") {
          spriteData.valueTextBlurred.scale.set(1);
          spriteData.valueTextBlurred.updateText();

          const coinWidth = this.symbolWidth;
          const paddingPercent = 0.1;
          const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

          const actualTextWidth = spriteData.valueTextBlurred.width;

          // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
          if (actualTextWidth > maxTextWidth) {
            const scale = maxTextWidth / actualTextWidth;
            spriteData.valueTextBlurred.scale.set(scale);
          }
        }
      }
    } else if (spriteData.valueText) {
      // Non-coin symbol: clear text without heavy processing
      spriteData.valueText.text = "";
      spriteData.valueText.alpha = 0;
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = "";
        spriteData.valueTextBlurred.alpha = 0;
      }
    }
  }

  // Checks if any coin exists within MIN_COIN_SPACING positions below the top sprite (index 0).
  // After wrapping, index 0 is the newest sprite at the top. Indices 1, 2, etc. are below it.
  // This ensures coins are spaced apart in the strip, preventing visual stacking.
  _hasCoinWithinSpacing(spacing) {
    const limit = Math.min(spacing, this.spinningSprites.length);
    for (let i = 1; i < limit; i++) {
      const id = this.spinningSprites[i]?.currentSymbolId;
      if (id && isCoinSymbol(id)) return true;
    }
    return false;
  }

  // Checks if any zeus orb exists within the given spacing positions

  // Generates random symbol for spin animation (cosmetic only — API controls landing).
  // Uses FEATURES_CONFIG.COIN_GENERATION to determine:
  //   - Which reels can show coins / royals
  //   - Probability per symbol position
  //   - Minimum spacing between coins
  _generateRandomSymbolId() {
    const coinConfig = FEATURES_CONFIG.COIN_GENERATION;

    // NOTE: a Zeus injection used to sit here, forcing 8% of cells to
    // BONUS_SYMBOL_IDS. In Zeus those were the orbs; in Mad Labs that id is
    // the GOLDEN MYSTERY, which invariant I3 says can never land directly —
    // so the blur was showing a symbol the math cannot produce. Removed.

    // No coin generation configured — base symbols only
    if (!coinConfig || !coinConfig.MODE) {
      return this._pickRandomSymbolId();
    }

    // Royal symbol — only on configured reels
    if (
      ROYAL_SYMBOL_ID &&
      coinConfig.ROYAL_REELS?.includes(this.reelIndex) &&
      Math.random() < (coinConfig.ROYAL_PROBABILITY || 0)
    ) {
      return ROYAL_SYMBOL_ID;
    }

    // Coin symbols — only on configured reels, with spacing constraint
    if (
      COIN_SYMBOL_IDS.length > 0 &&
      coinConfig.COIN_REELS?.includes(this.reelIndex)
    ) {
      // Enforce minimum spacing between coins
      const spacing = coinConfig.MIN_COIN_SPACING || 1;
      if (spacing > 1 && this._hasCoinWithinSpacing(spacing)) {
        return this._pickRandomSymbolId();
      }

      if (Math.random() < (coinConfig.PROBABILITY || 0)) {
        return COIN_SYMBOL_IDS[
          Math.floor(Math.random() * COIN_SYMBOL_IDS.length)
        ];
      }
    }

    return this._pickRandomSymbolId();
  }

  // Initializes textures - visible sprites use API data, buffers use random symbols
  _updateSpinningSprites() {
    if (!this.spinningSprites.length) return;

    const shouldSkipSprite0 =
      this.pendingLandingSymbolId !== null && this._isLandingSpriteVisible();

    // Check if we have initial reels data from API
    const hasInitialReels =
      this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === 0 && shouldSkipSprite0) continue;

      let symbolId;

      if (
        hasInitialReels &&
        i >= VISIBLE_SYMBOL_START_INDEX &&
        i < VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
      ) {
        const reelIndex = i - VISIBLE_SYMBOL_START_INDEX;
        symbolId = this.reels[reelIndex];
        this._setSpriteTexture(
          this.spinningSprites[i],
          symbolId,
          this._landingCoinValue(reelIndex),
          this._landingJackpotSkin(reelIndex),
        );
        continue;
      } else {
        symbolId = this._generateRandomSymbolId();
      }

      this._setSpriteTexture(this.spinningSprites[i], symbolId);
    }
  }

  _resetSpritePositionsForSpin() {
    if (!this.spinningSprites.length) return;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }
    this.reelYOffset = 0; // absolute placement — see the note in spin()
  }

  /**
   * Move the reel's column. Previously a parent container's x; now pushed to
   * each sprite, so it must be re-applied whenever reelSpacing changes.
   */
  setReelXOffset(nextOffset) {
    if (this.reelXOffset === nextOffset) return;
    this.reelXOffset = nextOffset;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (sprite?.container) sprite.container.x = nextOffset;
    }
  }

  /**
   * Shift the whole reel vertically, exactly as translating the container did.
   * Delta-based on purpose: each sprite keeps its own relative position, so
   * this is safe whether the reel is at rest or mid-wrap.
   */
  _setReelYOffset(nextOffset) {
    const delta = nextOffset - this.reelYOffset;
    if (delta === 0) return;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (sprite?.container) sprite.container.y += delta;
    }
    this.reelYOffset = nextOffset;
    this._cullOffscreenSprites();
  }

  // Checks if landing zone sprites are visible - determines when to apply API landing symbols
  _isLandingSpriteVisible() {
    if (!this.spinningContainer || this.spinningSprites.length < SPRITE_COUNT) {
      return false;
    }

    const visibleTop = -this.symbolHeight / 2;
    const visibleBottom = this.symbolHeight / 2;
    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    // Check if all landing zone sprites (1-4) are within visible bounds
    for (let i = landingZoneStart; i < landingZoneEnd; i++) {
      const sprite = this.spinningSprites[i];
      if (!sprite) return false;

      const absoluteY = sprite.container.y;
      if (absoluteY < visibleTop || absoluteY > visibleBottom) {
        return false;
      }
    }
    return true;
  }

  // Applies API landing symbols to landing zone - sprites drop into visible area on stop
  _setLandingSymbolOnVisibleSprites(symbolId = null) {
    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    if (this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT) {
      for (let i = landingZoneStart; i < landingZoneEnd; i++) {
        if (this.spinningSprites.length > i) {
          const apiIndex = i - 1;
          const symbolIdForThisPosition = this.reels[apiIndex];

          if (
            symbolIdForThisPosition !== undefined &&
            symbolData[symbolIdForThisPosition]
          ) {
            this._setSpriteTexture(
              this.spinningSprites[i],
              symbolIdForThisPosition,
              this._landingCoinValue(apiIndex),
              this._landingJackpotSkin(apiIndex),
            );
          }
        }
      }
    } else if (symbolId !== null) {
      for (let i = landingZoneStart; i < landingZoneEnd; i++) {
        if (this.spinningSprites.length > i) {
          this._setSpriteTexture(this.spinningSprites[i], symbolId);
        }
      }
    }
  }

  // Start spin with wind-up animation
  spin() {
    if (this.isPlaying || this.isWindingUp) return;

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    // DON'T reset expanding wild flag here - let it persist until new API data arrives
    // This prevents blinking when spinning after expanding wild
    // Flag will be reset in updateReels() when new data arrives from API

    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
      // Sprites were just placed absolutely, so any leftover offset is already
      // gone from the display. Clearing the bookkeeping keeps the delta-based
      // _setReelYOffset from subtracting it a second time — reachable when a
      // freeze interrupts the wind-up and leaves reelYOffset non-zero.
      this.reelYOffset = 0;
    }

    this._showBufferSprites();
    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    if (FEATURES_CONFIG.SPIN.WIND_UP_DURATION === 0) {
      this._startSpinAnimation();
      return;
    }

    this.isWindingUp = true;
    this._pendingStopKind = null;

    // ── roundPixels stays ON, as it does in Zeus ──
    //
    // It was turned off here while the wind-up ran at 276ms — half Zeus's
    // speed — where the easeInCubic return leg opened with frames that
    // advanced under one pixel and rounded to the same y: the reel sat still,
    // then jumped. The wind-up is back on Zeus's clock and Zeus's depth
    // (FEATURES_CONFIG.SPIN.WIND_UP_*), so those frames move as far as Zeus's
    // do and rounding costs nothing. setRoundPixels() is kept for the next
    // time something genuinely moves sub-pixel.

    // Wind-up distance and duration match the bounce overshoot exactly.
    // easeOutBack overshoots by 4s³/(27(s+1)²) of total landing distance.
    // Bounce portion takes 2s/(3(s+1)) fraction of total stop time.
    const speedConfig = this._getSpeedConfig();
    const s = speedConfig.BOUNCE_OVERSHOOT;
    const overshootFraction = (4 * s * s * s) / (27 * (s + 1) * (s + 1));
    const windUpDistanceMultiplier = FEATURES_CONFIG.SPIN.WIND_UP_DISTANCE_MULTIPLIER ?? 1.0;
    const windUpDistance =
      -(overshootFraction * STOP_MOVE_DISTANCE_SYMBOLS * windUpDistanceMultiplier) *
      this.symbolHeight;

    // Calculate bounce duration from the stop timing to match wind-up timing
    const spinVelocity =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    const totalStopDuration =
      (((s + 3) * STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight * 1000) /
        spinVelocity) *
      speedConfig.STOP_SLOWDOWN_FACTOR;
    const bounceFraction = (2 * s) / (3 * (s + 1));
    const windUpSpeedFactor = FEATURES_CONFIG.SPIN.WIND_UP_SPEED_FACTOR ?? 1.0;
    const windUpDuration =
      Math.round(totalStopDuration * bounceFraction * windUpSpeedFactor) ||
      FEATURES_CONFIG.SPIN.WIND_UP_DURATION;

    const currentY = this.reelYOffset;
    const originalY = 0;

    if (Math.abs(currentY) > 0.001) {
      this._setReelYOffset(originalY);
    }

    // Wind-up: pull up, then release back to origin.
    // Spin starts in the complete callback — zero overlap between anime.js and Ticker rAF loops.
    // Tweens a plain scalar and pushes it onto the sprites each frame — same
    // pattern the stop animation already uses with its {progress:0} target.
    // Keyframe values and easings are unchanged, so the wind-up feel is
    // identical; only what moves underneath it changed.
    this._reelYTweenTarget.y = originalY;
    this.windUpAnimation = anime({
      targets: this._reelYTweenTarget,
      update: () => {
        this._setReelYOffset(this._reelYTweenTarget.y);
      },
      y: [
        {
          value: originalY + windUpDistance,
          duration: windUpDuration,
          easing: "easeOutCubic",
        },
        {
          value: originalY,
          duration: windUpDuration * 0.6,
          easing: "easeInCubic",
        },
      ],
      complete: () => {
        this._setReelYOffset(originalY);
        this.windUpAnimation = null;

        // Mark wind-up as done BEFORE dispatching — manual stop checks this flag
        this.isWindingUp = false;

        // Start spin animation FIRST — highest priority, no frame delay
        this._startSpinAnimation();

        // Replay a stop that arrived mid-wind-up. AFTER _startSpinAnimation,
        // because every stop bails unless isPlaying is set.
        const pendingStop = this._pendingStopKind;
        if (pendingStop) {
          this._pendingStopKind = null;
          if (pendingStop === "turbo") this.stopSpinTurbo();
          else if (pendingStop === "tension") this.stopSpinTension();
          else this.stopSpin();
        }

        // Defer Redux dispatch to AFTER paint — setTimeout(0) allows React 18 to batch
        // queueMicrotask runs before paint, causing 5 separate React re-renders per frame
        setTimeout(() => {
          this.dispatch(incrementWindupCount());
        }, 0);
      },
    });
  }

  // Start spin without wind-up (used for quick mode)
  spinNoWindUp() {
    if (this.isPlaying || this.isWindingUp) return;

    this.clearAllTimeouts();
    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
      // Sprites were just placed absolutely, so any leftover offset is already
      // gone from the display. Clearing the bookkeeping keeps the delta-based
      // _setReelYOffset from subtracting it a second time — reachable when a
      // freeze interrupts the wind-up and leaves reelYOffset non-zero.
      this.reelYOffset = 0;
    }

    this._showBufferSprites();
    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    // Jump straight to spinning — FIRST, before any dispatch
    this._startSpinAnimation();

    // Defer Redux dispatch to AFTER paint
    setTimeout(() => {
      this.dispatch(incrementWindupCount());
    }, 0);
  }

  // Start turbo spin (no wind-up, no animation, instant setup)
  spinTurbo() {
    if (this.isPlaying || this.isWindingUp) return;

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    // DON'T reset expanding wild flag here - let it persist until new API data arrives
    // This prevents blinking when spinning after expanding wild
    // Flag will be reset in updateReels() when new data arrives from API

    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    this.hasStopped = false;
    this.isStopping = false;
    this._wasManualStopped = false;
    this.isBlurred = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
    this._spinElapsed = 0;

    this._switchToNormalTextures();

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    this._showBufferSprites();
    this.reelYOffset = 0; // sprites were just placed absolutely by the loop above

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    this.isPlaying = true;
  }

  // Stop turbo spin (instant drop with bounce)
  stopSpinTurbo() {
    if (this._deferStopDuringWindUp("turbo")) return;
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;

    this.isPlaying = false;
    this.stopTweenActive = true;

    // Stop the spin ticker — prevent competing rAF loops during drop animation
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Unblur all sprites so landing symbols are sharp during drop
    this._switchToNormalTextures();

    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    for (let i = landingZoneStart; i < landingZoneEnd; i++) {
      const apiIndex = i - 1;
      const symbolId = this.reels[apiIndex];
      if (symbolId !== undefined) {
        this._setSpriteTexture(
          this.spinningSprites[i],
          symbolId,
          this._landingCoinValue(apiIndex),
          this._landingJackpotSkin(apiIndex),
        );
      }
    }

    const dropDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const turboConfig = FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO;
    const dropDuration = turboConfig.DROP_DURATION || 300;
    const bounceOvershoot = turboConfig.BOUNCE_OVERSHOOT ?? 1.5; // Use nullish coalescing to allow 0

    let lastProgress = 0;
    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = dropDistance;

    this.activeStopAnimation = anime({
      targets: { progress: 0 },
      progress: dropDistance,
      duration: dropDuration,
      easing: function () {
        return function (t, overshoot = bounceOvershoot) {
          return --t * t * ((overshoot + 1) * t + overshoot) + 1;
        };
      },
      update: (anim) => {
        const currentProgress = anim.animations[0].currentValue;
        const delta = currentProgress - lastProgress;
        lastProgress = currentProgress;

        this.stopAnimationProgress = currentProgress / dropDistance;

        if (Math.abs(delta) < 0.0001) return;

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        // Use cached wrap threshold
        const wrapThreshold =
          this._wrapThresholdCache || SPRITE_COUNT * this.symbolHeight;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }

        this._cullOffscreenSprites();
      },
      complete: () => {
        this.activeStopAnimation = null;
        this.stopAnimationProgress = 0;

        // Apply deferred resize if pending
        if (this._pendingPositionUpdate) {
          const wasStopped = this.hasStopped;
          const wasStopTweenActive = this.stopTweenActive;
          this.stopTweenActive = false;
          this.activeStopAnimation = null;

          this._applyPendingResize();

          this.hasStopped = wasStopped;
          this.stopTweenActive = wasStopTweenActive;
        }

        // Rotate array: move last N sprites to front (single O(n) operation)
        const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE_SYMBOLS;
        const tail = this.spinningSprites.splice(cut);
        this.spinningSprites.unshift(...tail);

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y = i * this.symbolHeight;
        }

        // Set critical state flags immediately
        this.reelYOffset = 0; // sprites were just placed absolutely by the loop above
        this.hasStopped = true;
        this.isStopping = false;
        this.stopTweenActive = false;
        this.lockedSpinSpeed = null;

        this._hideBufferSprites();

        // 🚀 FIRE LANDING ANIMATIONS IMMEDIATELY - highest priority for smoothest UX
        if (this.onReelStop) {
          this.onReelStop(this.reelIndex, this.reels);
        }

        // Play sound when animation completes — reel_stop once + special symbol sound
        // In tension mode, the last non-tension reel plays the consolidated sound
        // instead of waiting for reel 4 (which is in tension)
        if (this.reelIndex === Settings.REEL_COUNT - 1 || this.isLastNonTensionReel) {
          const specialSound = this._getTurboModeSound();
          if (specialSound && specialSound !== SOUND_NAME.reel_stop) {
            // Wild/scatter/coin present — play only the special sound, skip reel_stop
            soundManager.play(specialSound);
          } else {
            // No special symbols — play a single reel_stop
            soundManager.play(SOUND_NAME.reel_stop);
          }
        }

        if (this.reelIndex === Settings.REEL_COUNT - 1) {
          this._completeSpin();
        }

        // Reset tension flag
        this.isLastNonTensionReel = false;
      },
    });
  }

  // Starts spin animation after wind-up completes
  _startSpinAnimation() {
    // Every label this spin can show, drawn BEFORE anything moves (keyed).
    this._warmCoinLabels();
    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this._wasManualStopped = false;
    this.isBlurred = false;
    this.stopTweenActive = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
    this.isDecelerating = false;
    this.accelerationMultiplier = 0; // Start from 0, ramp up
    this.currentVelocity = 0;

    // Reset manual stop sound flag on first reel spin start
    if (this.reelIndex === 0) {
      ReelControllerV2._manualStopSoundPlayed = false;
    }

    // Reset tension deceleration for new spin
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Start reel_spin sound when first reel begins actual spinning (after wind-up)
    // Skip in super_turbo — spin is too fast for a looping sound to be audible
    if (this.reelIndex === 0 && !isSuperTurbo(this.autoPlaySpeed)) {
      soundManager.play(SOUND_NAME.reel_spin);
    }

    const speedConfig = this._getSpeedConfig();
    // Clamp speed to safe minimum: at 60fps each frame is ~16.7ms,
    // so speed must be >= 17ms/symbol to keep movement <= 1 symbolHeight/frame
    const safeSpeedMs = Math.max(speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT, 17);
    this.normalizedSpinSpeed =
      (this.symbolHeight / safeSpeedMs) * 1000;
    this._spinElapsed = 0;
    this.lastCalculatedPosition = 0;

    // No timer-based blur — velocity-based blur handles it in updateSymbolsAbsolute()

    this.tickerCallback = () => {
      if (!this.isPlaying && !this.stopTweenActive) {
        return;
      }
      this.updateSymbolsAbsolute();
    };

    PIXI.Ticker.shared.add(this.tickerCallback);
  }

  // Initiates stop sequence - cannot stop during wind-up
  /**
   * Hold a stop that arrived before the wind-up finished.
   *
   * A stop request is fire-and-forget: the TickerTimeout that raised it is
   * already gone by the time it lands here, so a dropped request is never
   * retried and the reel spins for ever.
   *
   * This is easy to hit because `isPlaying` is FALSE for the whole wind-up —
   * _startSpinAnimation only sets it in the wind-up's complete callback — so
   * every stop entry point's `!isPlaying` guard silently swallows the request.
   *
   * The live case was reel 0 in AUTO. It is the only reel with a zero stop
   * delay (REEL_STOP_DELAYS_AUTO starts at 0) and AUTO is the only speed that
   * winds up; the rest set SKIP_WIND_UP. It appeared at 5x6 because the wind-up
   * derives from STOP_MOVE_DISTANCE_SYMBOLS, which is SYMBOLS_VISIBLE: 3 -> 6
   * rows doubled it from ~220ms to ~440ms, so it began outlasting the ~400ms
   * stop trigger. Nothing here assumes a row count — a longer wind-up on a
   * taller grid, or a faster API, just means more reels take this path.
   *
   * Guarded on all three entry points rather than only the one that broke:
   * turbo and tension are reachable from Reel.jsx and are safe today purely
   * because of feature flags, which is not a property worth relying on.
   *
   * @returns {boolean} true when the caller should return immediately
   */
  _deferStopDuringWindUp(kind) {
    if (!this.isWindingUp) return false;
    // First request wins — a later one cannot downgrade an earlier stop.
    if (!this._pendingStopKind) this._pendingStopKind = kind;
    return true;
  }

  stopSpin() {
    if (this._deferStopDuringWindUp("normal")) return;

    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;
    this._wasManualStopped = !!store.getState().spin.manualStop;

    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
  }

  // Tension mode — gradually decelerates the reel to build anticipation.
  // The reel smoothly slows from full speed to TARGET_SPEED over a duration
  // derived from the tension spine cycle: cycleDuration × DECELERATION_RATIO.
  // Blur naturally fades out as velocity drops below BLUR_VELOCITY_THRESHOLD.
  spinTension() {
    if (!this.isPlaying || this.isStopping || this.hasStopped) return;
    this.isTensionMode = true;

    const tensionDecel = FEATURES_CONFIG.TENSION.REEL_DECELERATION;
    if (tensionDecel?.ENABLED) {
      // Derive deceleration duration from spine data × ratio (no hardcoded ms)
      const cycleDuration = SpineAnimationData.getTensionCycleDuration()
        || FEATURES_CONFIG.TENSION.DURATION; // Fallback to config if spine not cached yet
      this._tensionDecelerationDuration = cycleDuration * (tensionDecel.DECELERATION_RATIO ?? 0.45);
      this._tensionDecelerating = true;
      this._tensionDecelElapsed = 0;
      this._tensionSpeedMultiplier = 1.0; // Start at current speed, ramps down gradually
    }
  }

  // Stops tension mode spin — just triggers normal stop
  // Marks reel as tension so _finalizeLanding can play per-reel sounds (not consolidated turbo sound)
  stopSpinTension() {
    if (this._deferStopDuringWindUp("tension")) return;
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;
    this.isStopping = true;

    this.isTensionMode = true;
  }

  // Freeze spin instantly - stops all animations without landing
  // Used for error popups where reels should freeze at current position
  freezeSpin() {
    // Stop ticker immediately
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Stop wind-up if active
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this._reelYTweenTarget); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
      this.isWindingUp = false;
      this._pendingStopKind = null;
    }

    // Clear ticker-based stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;

    // Set state flags
    this.isPlaying = false;
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.isTensionMode = false;
    this.speedMultiplier = 1.0;
    this._spinElapsed = 0;
    this.lockedSpinSpeed = null;
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;
    this.isDecelerating = false;

    // Reset tension deceleration state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Clear all pending timeouts
    this.clearAllTimeouts();

    // DO NOT call onReelStop - no landing animations
    // DO NOT dispatch setSpinComplete - popup handles flow
    // DO NOT switch textures - keep current blur state
  }

  // Pause/resume for tab switching — NO-OPS.
  // AppActivityContext sets PIXI.Ticker.shared.speed = 0 on tab hide,
  // which makes deltaMS = 0 for all subscribers. Since all timing now
  // accumulates deltaMS, everything freezes automatically.
  // anime.js wind-up/turbo-stop are rAF-based — also freeze when ticker pauses.
  // Methods kept for API compatibility with callers.
  pauseSpin() {}
  resumeSpin() {}


  // Restore reel to idle state with specific symbols
  // Used after error recovery to show previous round's symbols properly
  // @param {Array} symbolIds - Array of symbol IDs to restore
  // @param {number|null} betValue - Optional bet value to use for coin texts (for error recovery)
  restoreToIdleState(symbolIds, betValue = null) {
    // Clear all animations and timeouts
    this.clearAllTimeouts();

    // Stop ticker if active
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Stop any active animations
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this._reelYTweenTarget); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
      this.isWindingUp = false;
      this._pendingStopKind = null;
    }

    // Clear ticker-based stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;

    // Reset state flags
    this.isPlaying = false;
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.isTensionMode = false;
    this.isBlurred = false;
    this.speedMultiplier = 1.0;
    this._spinElapsed = 0;
    this.lockedSpinSpeed = null;
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;
    this.isDecelerating = false;

    // Reset tension deceleration state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Switch to normal (non-blurred) textures
    this._switchToNormalTextures();

    // Ensure sprites exist
    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
    }

    // Reset container position to default
    this.reelYOffset = 0; // the loop below places every sprite absolutely

    // Position all sprites to their default positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Set visible sprites to show the provided symbols
    // Visible sprites are at indices VISIBLE_SYMBOL_START_INDEX to VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
    if (symbolIds && Array.isArray(symbolIds)) {
      // Store original bet and temporarily use historical bet if provided
      const originalBet = this.bet;
      if (betValue !== null && betValue !== undefined) {
        this.bet = betValue;
      }

      for (let i = 0; i < VISIBLE_SYMBOL_COUNT && i < symbolIds.length; i++) {
        const spriteIndex = VISIBLE_SYMBOL_START_INDEX + i;
        const symbolId = symbolIds[i];
        if (symbolId !== undefined && this.spinningSprites[spriteIndex]) {
          this._setSpriteTexture(
            this.spinningSprites[spriteIndex],
            symbolId,
            this._landingCoinValue(i),
            this._landingJackpotSkin(i),
          );
        }
      }

      // Fill other sprites with random symbols for smooth next spin
      for (let i = 0; i < this.spinningSprites.length; i++) {
        if (
          i < VISIBLE_SYMBOL_START_INDEX ||
          i >= VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
        ) {
          const randomSymbolId = this._generateRandomSymbolId();
          this._setSpriteTexture(this.spinningSprites[i], randomSymbolId);
        }
      }

      // Update reels data for next spin
      this.reels = symbolIds;
      this.hasPendingLandingSymbols = false;

      // NOTE: Do NOT call this.onReelStop here.
      // onReelStop triggers full landing animations (including bolt trails)
      // which is wrong for error recovery — we just need the symbols placed.
      // The caller (ErrorRecoveryOverlay) triggers idle-only animations
      // via triggerInitialIdleAnimations() after all reels are restored.

      // Restore original bet after idle animations are triggered
      if (betValue !== null && betValue !== undefined) {
        this.bet = originalBet;
      }
    }

    this._hideBufferSprites();
  }

  // Toggle roundPixels on all sprites
  // Disable during tension zoom to prevent jitter from fractional container scaling
  // Re-enable after zoom completes for crisp 1:1 rendering
  setRoundPixels(enabled) {
    this.spinningContainer.roundPixels = enabled;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      sprite.container.roundPixels = enabled;
      sprite.normalSprite.roundPixels = enabled;
      sprite.blurredSprite.roundPixels = enabled;
      if (sprite.valueText) sprite.valueText.roundPixels = enabled;
      if (sprite.valueTextBlurred) sprite.valueTextBlurred.roundPixels = enabled;
    }
  }

  // Main animation loop
  updateSymbolsAbsolute() {
    if (this.hasStopped) {
      return;
    }

    // --- Ticker-based stop animation (replaces anime.js for seamless handoff) ---
    if (this.stopTweenActive && this._stopDuration > 0) {
      this._stopElapsed += PIXI.Ticker.shared.deltaMS;
      const elapsed = this._stopElapsed;
      const t = Math.min(elapsed / this._stopDuration, 1.0);

      // BackOut easing: f(t) = (t-1)² × ((s+1)(t-1) + s) + 1
      const s = this._stopOvershoot;
      const t1 = t - 1;
      const easedT = t1 * t1 * ((s + 1) * t1 + s) + 1;
      const easedPosition = this._stopDistance * easedT;
      const delta = easedPosition - this._stopLastEasedPos;
      this._stopLastEasedPos = easedPosition;

      this.stopAnimationProgress = easedPosition / this._stopDistance;

      // Update blur during stop (velocity decreases as easing decelerates)
      // BackOut derivative: f'(t) = 3(s+1)(t-1)² + 2s(t-1)
      const easingVelocity = 3 * (s + 1) * t1 * t1 + 2 * s * t1;
      this.currentVelocity = Math.abs(easingVelocity) * 0.3;
      this._updateBlurFromVelocity();

      if (Math.abs(delta) > 0.0001) {
        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        const wrapThreshold =
          this._wrapThresholdCache || SPRITE_COUNT * this.symbolHeight;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }

        this._cullOffscreenSprites();
      }

      // Detect overshoot peak (trough) — the exact frame where symbols reach their lowest point
      // BackOut peak: t_peak = 1 - 2s / (3(s+1)), only when s > 0 (has bounce)
      if (!this._landingPeakFired && this._stopOvershoot > 0) {
        const sPeak = this._stopOvershoot;
        const tPeakMath = 1 - (2 * sPeak) / (3 * (sPeak + 1));
        // LANDING_PEAK_OFFSET: 1.0 = exact trough, <1.0 = fires earlier
        const peakOffset = this._getSpeedConfig()?.LANDING_PEAK_OFFSET ?? 1.0;
        const tPeak = tPeakMath * peakOffset;
        if (t >= tPeak) {
          this._landingPeakFired = true;
          // The landing's sound, at the frame it lands — thud or announcement,
          // whichever this reel has earned. See _playLandingSound.
          // Skip during turbo/manual stop — quick_stop plays once on first force-stopped reel
          const skipPeakSound = this._wasManualStopped || isTurboLike(this.autoPlaySpeed);
          if (!skipPeakSound) {
            this._playLandingSound();
          }
          if (this.onLandingPeak) {
            this.onLandingPeak(this.reelIndex);
          }
        }
      }

      if (t >= 1.0) {
        this._finalizeLanding();
      }
      return;
    }

    // Accumulate elapsed time from ticker (freezes when tab hidden)
    this._spinElapsed += PIXI.Ticker.shared.deltaMS;
    const elapsedTime = this._spinElapsed / 1000;
    const currentExpectedPosition = elapsedTime * this.normalizedSpinSpeed;

    let movement = currentExpectedPosition - this.lastCalculatedPosition;
    this.lastCalculatedPosition = currentExpectedPosition; // Always track at full speed

    // --- Tension deceleration (smooth slowdown for anticipation) ---
    // Scales the per-frame visual movement while keeping position tracking at full speed.
    // This way the gap doesn't grow to compensate — the reel genuinely scrolls slower.
    if (this._tensionDecelerating) {
      const tensionDecel = FEATURES_CONFIG.TENSION.REEL_DECELERATION;
      this._tensionDecelElapsed += PIXI.Ticker.shared.deltaMS;
      const tensionElapsed = this._tensionDecelElapsed;
      const tDecel = Math.min(tensionElapsed / this._tensionDecelerationDuration, 1.0);
      // Ease-out cubic: starts slowing immediately, arrives at target speed gradually.
      // Produces a visible, dramatic deceleration from the very first frame.
      const t1 = 1 - tDecel;
      const easedDecel = 1 - (t1 * t1 * t1);
      this._tensionSpeedMultiplier = 1.0 - (1.0 - tensionDecel.TARGET_SPEED) * easedDecel;



      // Scale visual movement only — position tracker stays at full speed
      movement *= this._tensionSpeedMultiplier;
    }

    // --- Acceleration ramp-up (ease-out: fast start, smooth arrival at full speed) ---
    const speedConfig = this._getSpeedConfig();
    // Support ratio-based (ACCEL_SPEED_RATIO × SPEED_MS) or legacy absolute (ACCELERATION_DURATION)
    const accelDuration = speedConfig.ACCELERATION_DURATION
      ?? (speedConfig.ACCEL_SPEED_RATIO
        ? speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT * speedConfig.ACCEL_SPEED_RATIO
        : 0);
    if (accelDuration > 0 && this.accelerationMultiplier < 1.0) {
      const elapsedMs = this._spinElapsed;
      const t = Math.min(elapsedMs / accelDuration, 1.0);
      // Ease-out cubic: starts fast, smoothly reaches 1.0
      this.accelerationMultiplier = 1 - (1 - t) * (1 - t) * (1 - t);
    } else {
      this.accelerationMultiplier = 1.0;
    }

    // Apply acceleration multiplier
    movement *= this.accelerationMultiplier;

    // --- Blur: smooth per-frame velocity-based crossfade ---
    // During tension deceleration, velocity tracks the tension speed multiplier
    // → blur naturally fades out as speed drops below BLUR_VELOCITY_THRESHOLD
    this.currentVelocity = this._tensionDecelerating
      ? this._tensionSpeedMultiplier
      : this.accelerationMultiplier;
    this._updateBlurFromVelocity();

    // Cache wrap threshold calculation
    if (
      this._wrapThresholdCache === null ||
      this._lastSymbolHeightForWrapThreshold !== this.symbolHeight
    ) {
      this._wrapThresholdCache = SPRITE_COUNT * this.symbolHeight;
      this._lastSymbolHeightForWrapThreshold = this.symbolHeight;
    }
    const wrapThreshold = this._wrapThresholdCache;
    const shouldUpdateTexture = !this.isStopping && !this.hasStopped;

    if (
      this.pendingLandingSymbolId !== null &&
      !this._isLandingSpriteVisible()
    ) {
      this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
      this.pendingLandingSymbolId = null;
    }

    let shouldExecuteStop = false;

    // Phase 1: Apply movement to ALL sprites uniformly (no break-and-snap)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y += movement;
    }

    this._cullOffscreenSprites();

    // Phase 2: Check for wraps AFTER all sprites have moved
    // While loop handles multiple wraps per frame at high speeds (safety for extreme SPEED_MS values)
    let wrapsThisFrame = 0;
    const lastIndex = this.spinningSprites.length - 1;
    while (
      this.spinningSprites[lastIndex].container.y >= wrapThreshold &&
      wrapsThisFrame < SPRITE_COUNT // Safety cap: can never need more wraps than sprites
    ) {
      wrapsThisFrame++;

      if (this._pendingPositionUpdate) {
        this._applyPendingResize();
      }

      this.moveLastSymbolOnTop();

      if (
        this.pendingLandingSymbolId !== null &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
        this.pendingLandingSymbolId = null;
      }

      if (shouldUpdateTexture) {
        // Update texture on the sprite that just wrapped to top (now index 0)
        this.updateSymbolTextureRandomly(this.spinningSprites[0]);
      }

      if (this.isStopping && !this.hasStopped && !this.stopTweenActive) {
        shouldExecuteStop = true;
      }
    }

    if (shouldExecuteStop) {
      this._calculateAndExecuteStop();
    }
  }

  // Wraps last sprite to top — single-sprite teleport (no grid-snap on other sprites)
  moveLastSymbolOnTop() {
    const movedSymbol = this.spinningSprites.pop();
    this.spinningSprites.unshift(movedSymbol);

    // Teleport only the wrapped sprite to the top position.
    // Other sprites keep their exact Y positions — no grid-snap stutter.
    // The total strip height is the distance from top to wrap threshold.
    const totalStripHeight = SPRITE_COUNT * this.symbolHeight;
    movedSymbol.container.y -= totalStripHeight;
  }

  // Calculates current drift from expected positions
  _calculateCurrentDrift() {
    if (!this.spinningSprites.length) return 0;

    const visibleStartIndex = VISIBLE_SYMBOL_START_INDEX;
    const visibleEndIndex = visibleStartIndex + VISIBLE_SYMBOL_COUNT;

    let totalDrift = 0;
    let count = 0;

    for (
      let i = visibleStartIndex;
      i < visibleEndIndex && i < this.spinningSprites.length;
      i++
    ) {
      const expectedY = i * this.symbolHeight;
      const actualY = this.spinningSprites[i].container.y;
      const drift = actualY - expectedY;
      totalDrift += drift;
      count++;
    }

    const averageDrift = count > 0 ? totalDrift / count : 0;
    return averageDrift;
  }

  // Updates sprite with random symbol
  updateSymbolTextureRandomly(spriteData) {
    const randomSymbolId = this._generateRandomSymbolId();
    this._setSpriteTexture(spriteData, randomSymbolId);
  }

  // Initializes textures for spinning sprites
  _initializeSpinningTextures() {
    // ── Rebuilt here if the sheet was not ready at construction ──
    //
    // Off the hot path on purpose: this runs once per spin start, not per
    // recycled cell. An empty table is not an error — every lookup falls back
    // to the old string path and behaves exactly as before — but it would mean
    // paying that cost for the whole session, so it is worth one check here.
    if (this._textureTable?.size === 0 && this.spritesheet?.textures) {
      this._textureTable = this._buildTextureTable();
    }

    this.spinningSprites.forEach((spriteData, index) => {
      const randomSymbolId = this._generateRandomSymbolId();
      this._setSpriteTexture(spriteData, randomSymbolId);
    });
    this.hasInitializedTextures = true;
  }

  /**
   * The ONE sound this reel makes as it lands.
   *
   * ── One INSTEAD of the other, never both ──
   *
   * A reel carrying a scatter or a mystery announces THAT and does not also
   * thud. `reel_stop` is what a reel says when it has nothing to say.
   *
   * ── And on the IMPACT frame, not the settle ──
   *
   * This used to be split in two: `reel_stop` fired at the overshoot peak, the
   * frame the symbols hit bottom, while the special sound waited for
   * _finalizeLanding at t=1.0. The bounce is 367-667ms, so the announcement
   * arrived a visible beat after the thud it was supposed to replace. Both now
   * go through here, so wherever it is called from they are the same moment.
   *
   * playOncePerFrame on the special, because turbo and a manual stop land every
   * reel together and two reels carrying a mystery would play it twice on top
   * of itself. Keyed by NAME, so a scatter on one reel and a mystery on another
   * still both sound.
   *
   * @param {object}  [opts]
   * @param {boolean} [opts.thud] False where `quick_stop` is already the
   *   landing's own sound — turbo and manual stop — so only the announcement
   *   is added and the reel is not thudded twice.
   */
  _playLandingSound({ thud = true } = {}) {
    const special = this._getReelStopSound();
    if (special && special !== SOUND_NAME.reel_stop) {
      soundManager.playOncePerFrame(special);
      return;
    }
    if (thud) soundManager.play(SOUND_NAME.reel_stop);
  }

  /**
   * Does a MYSTERY lid stand on this reel?
   *
   * Asked directly rather than through _getReelStopSound, which returns ONE
   * sound by priority: a reel holding a scatter AND a mystery answers
   * `scatter_land`, and the mystery would go unheard in the one mode that
   * depends on hearing it.
   *
   * @returns {boolean}
   */
  /**
   * The symbols that will be standing in the VISIBLE window when this reel has
   * settled — the landing data, not the sprites.
   *
   * ── Why not read the sprites ──
   *
   * `spinningSprites` is only trustworthy once the reel has come to rest. The
   * landing sound is fired at the OVERSHOOT PEAK, the frame the symbols hit
   * bottom, so that the announcement lands with the thud instead of a beat
   * after it. At that instant the strip is past its final position and the
   * slots at the visible indices still hold symbols that are about to slide
   * back OUT of view — so a scatter one row below the window announced itself
   * and then never appeared.
   *
   * `this.reels` is the API's landing data for this reel and its first
   * VISIBLE_SYMBOL_COUNT entries are exactly the visible rows (see
   * _setLandingSymbolOnVisibleSprites, which paints from the same indices). It
   * does not move, so the answer is the same whenever it is asked.
   *
   * Falls back to the settled sprites when there is no landing data — an idle
   * board, or a reel that was never given API symbols.
   *
   * @returns {number[]} visible symbol ids, may be empty
   */
  _visibleLandedSymbolIds() {
    const ids = [];
    if (Array.isArray(this.reels) && this.reels.length >= VISIBLE_SYMBOL_COUNT) {
      for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) ids.push(this.reels[i]);
      return ids;
    }
    for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
      ids.push(this.spinningSprites[VISIBLE_SYMBOL_START_INDEX + i]?.currentSymbolId);
    }
    return ids;
  }

  _hasLandedMystery() {
    for (const symbolId of this._visibleLandedSymbolIds()) {
      if (symbolId === undefined || symbolId === null) continue;
      if (symbolData[symbolId]?.symbolPay === "mystery") return true;
    }
    return false;
  }

  _getReelStopSound() {
    let hasScatter = false;
    let hasMystery = false;

    // The board as it will SETTLE — see _visibleLandedSymbolIds for why the
    // sprites cannot be trusted at the moment this is asked.
    for (const symbolId of this._visibleLandedSymbolIds()) {
      if (symbolId === undefined || symbolId === null) continue;

      const symInfo = symbolData[symbolId];
      if (!symInfo) continue;

      if (symInfo.symbolPay === "scatter") {
        hasScatter = true;
      } else if (symInfo.symbolPay === "mystery") {
        hasMystery = true;
      }
    }

    if (hasScatter) return SOUND_NAME.scatter_land;
    if (hasMystery) return SOUND_NAME.mystery_land;
    // ── WILD and COIN are NOT announcements, and never were ──
    //
    // They used to sit here as `SOUND_NAME.wild` and `SOUND_NAME.coin`, with a
    // comment calling them "names with no file yet". They are worse than that:
    // sounds.js defines NEITHER, so both were `undefined` — this function
    // returned undefined for any board holding a wild, and every caller only
    // survived it by testing the result for truthiness first. One stricter
    // caller and the landing would have gone silent.
    //
    // Neither is owed a sound of its own:
    //   wild  has no file and no plan for one — a wild is just a symbol landing.
    //   coin  appears only inside the Golden Mystery, where the CELL announces
    //         it with `coin_land` (goldenMysteryConfig, goldenLandingSound).
    //         The reel saying it too would double every coin.
    //
    // So both fall through to the thud, which is what a reel says when it has
    // nothing to say. Vladimir, 2026-09-18: "there is no sound for wild now or
    // coins so its only mystery or stop, thats a bug".
    return SOUND_NAME.reel_stop;
  }

  // Determines turbo mode stop sound by checking all reels - used on last reel
  _getTurboModeSound() {
    const state = store.getState();
    if (
      !state ||
      !state.game ||
      !state.game.currentRound ||
      !state.game.currentRound.reels
    ) {
      return this._getReelStopSound();
    }

    const allReels = state.game.currentRound.reels;
    let hasScatter = false;
    let hasMystery = false;

    for (let reelIndex = 0; reelIndex < allReels.length; reelIndex++) {
      const reel = allReels[reelIndex];
      if (!reel) continue;

      for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
        const symbolId = reel[i];
        if (symbolId === undefined) continue;

        const symInfo = symbolData[symbolId];
        if (!symInfo) continue;

        if (symInfo.symbolPay === "scatter") {
          hasScatter = true;
        } else if (symInfo.symbolPay === "mystery") {
          hasMystery = true;
        }
      }

      // The scan used to STOP on the first wild, because a wild answered. It
      // does not any more — and stopping there could skip a scatter or a
      // mystery on a later reel, which is the one thing this function exists
      // to find. Every reel is read now.
    }

    if (hasScatter) return SOUND_NAME.scatter_land;
    if (hasMystery) return SOUND_NAME.mystery_land;
    // ── WILD and COIN are NOT announcements, and never were ──
    //
    // They used to sit here as `SOUND_NAME.wild` and `SOUND_NAME.coin`, with a
    // comment calling them "names with no file yet". They are worse than that:
    // sounds.js defines NEITHER, so both were `undefined` — this function
    // returned undefined for any board holding a wild, and every caller only
    // survived it by testing the result for truthiness first. One stricter
    // caller and the landing would have gone silent.
    //
    // Neither is owed a sound of its own:
    //   wild  has no file and no plan for one — a wild is just a symbol landing.
    //   coin  appears only inside the Golden Mystery, where the CELL announces
    //         it with `coin_land` (goldenMysteryConfig, goldenLandingSound).
    //         The reel saying it too would double every coin.
    //
    // So both fall through to the thud, which is what a reel says when it has
    // nothing to say. Vladimir, 2026-09-18: "there is no sound for wild now or
    // coins so its only mystery or stop, thats a bug".
    return SOUND_NAME.reel_stop;
  }

  // Sets win state for this spin - affects sound playback
  setHasWinThisSpin(hasWin) {
    this.hasWinThisSpin = !!hasWin;
  }

  // Sets Hold & Win trigger state for this spin - affects spin complete delay
  setIsHoldAndWinTrigger(isTrigger) {
    this.isHoldAndWinTrigger = !!isTrigger;
  }

  // Gets speed configuration based on locked or current autoPlaySpeed
  // Cached for performance - invalidated when speed changes
  _getSpeedConfig() {
    const effectiveSpeed = this.lockedSpinSpeed || this.autoPlaySpeed;

    // Check cache validity
    if (
      this._speedConfigCache !== null &&
      this._lastLockedSpeed === this.lockedSpinSpeed &&
      this._lastAutoPlaySpeed === this.autoPlaySpeed
    ) {
      return this._speedConfigCache;
    }

    const config =
      FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[effectiveSpeed?.toUpperCase()] ||
      FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;

    // Cache result
    this._speedConfigCache = config;
    this._lastLockedSpeed = this.lockedSpinSpeed;
    this._lastAutoPlaySpeed = this.autoPlaySpeed;

    return config;
  }

  // Unblur all sprites - instant transition
  _switchToNormalTextures() {
    if (!this.spritesheetBlurred) {
      return Promise.resolve();
    }

    this.spinningSprites.forEach((sprite) => {
      if (!sprite || !sprite.blurredSprite) return;
      sprite.normalSprite.alpha = 1;
      sprite.blurredSprite.alpha = 0;

      // Switch text back to normal
      if (sprite.valueText && sprite.valueText.text !== "") {
        sprite.valueText.alpha = 1;
      }
      if (sprite.valueTextBlurred) {
        sprite.valueTextBlurred.alpha = 0;
      }
    });

    this.isBlurred = false;

    return Promise.resolve();
  }

  // Selectively unblurs landing symbols during stop animation
  _switchToNormalTexturesSelective() {
    if (!this.spritesheetBlurred) return;

    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
    const bufferAbove = landingZoneStart - 1;
    const bufferBelow = landingZoneEnd;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.blurredSprite) continue;

      const isLandingZone = i >= landingZoneStart && i < landingZoneEnd;
      const isBufferAbove = i === bufferAbove;
      const isBufferBelow = i === bufferBelow;

      if (isLandingZone || isBufferAbove || isBufferBelow) {
        spriteData.normalSprite.alpha = 1;
        spriteData.blurredSprite.alpha = 0;

        // Switch text to normal for landing symbols
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }
    }

    this.isBlurred = false;
  }

  // Proportional blur: sets alpha of normal/blurred sprites based on velocity
  // Below threshold = fully sharp, above = gradually blurred proportional to speed
  _updateBlurFromVelocity() {
    if (!this.spritesheetBlurred) return;

    const speedConfig = this._getSpeedConfig();
    const threshold = speedConfig.BLUR_VELOCITY_THRESHOLD || 0.6;
    const velocity = this.currentVelocity;

    // Calculate blur amount: 0 = fully sharp, 1 = fully blurred
    // Ramp from threshold to 1.0 (full speed)
    let blurAmount;
    if (velocity < threshold) {
      blurAmount = 0;
    } else {
      // Normalized 0-1 within the blur range, squared for natural feel
      const t = (velocity - threshold) / (1.0 - threshold);
      blurAmount = Math.min(t * t, 1.0);
    }

    // Update isBlurred state for cleanup/state tracking
    this.isBlurred = blurAmount > 0.5;

    // ── A SWAP, not a crossfade ──
    //
    // Only one of the two sprites is ever drawn. The old version faded between
    // them AND floored the sharp one at 0.001 — so through the whole cruise,
    // where blur is at full, BOTH were submitted: 2 sprites per cell, twice the
    // fill, and 34 distinct textures against a batch limit that is 16 on most
    // GPUs and 8 on some mobiles. That is what made blur cost roughly double
    // the reel layer.
    //
    // Nothing is lost by swapping. The ramp spans about two frames at this
    // speed, so the crossfade was never visible; and at 0 exactly, PIXI skips
    // the hidden sprite outright rather than blending an invisible quad.
    //
    // The threshold is the midpoint of the ramp the velocity curve already
    // produces, so the swap lands where the crossfade used to be half way —
    // the moment the eye is least able to see it.
    const showBlurred = blurAmount > 0.5;
    const normalAlpha = showBlurred ? 0 : 1;
    const blurredAlpha = showBlurred ? 1 : 0;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.blurredSprite) continue;

      // During stop animation, skip landing zone + buffer sprites (already unblurred)
      if (this.stopTweenActive) {
        const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
        if (i >= 0 && i <= landingZoneEnd) continue;
      }

      spriteData.normalSprite.alpha = normalAlpha;
      spriteData.blurredSprite.alpha = blurredAlpha;

      // Blend coin value text alpha too
      if (spriteData.valueText && spriteData.valueText.text !== "") {
        spriteData.valueText.alpha = normalAlpha;
      }
      if (
        spriteData.valueTextBlurred &&
        spriteData.valueTextBlurred.text !== ""
      ) {
        spriteData.valueTextBlurred.alpha = blurredAlpha;
      }
    }
  }
  // Ticker-based landing animation — runs in the same PIXI ticker as spin
  // for seamless handoff (no frame gap between spin and stop)
  // Uses BackOut easing with configurable overshoot for bounce feel
  _calculateAndExecuteStop() {
    if (this.hasStopped || this.stopTweenActive) return;
    this.stopTweenActive = true;

    // DON'T remove the ticker — the stop animation runs inside it

    const hasApiData = this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    if (hasApiData) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites();
      }
    } else if (this.pendingLandingSymbolId !== null) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
        this.symbolId = this.pendingLandingSymbolId;
        this.pendingLandingSymbolId = null;
      }
    }

    const currentDrift = this._calculateCurrentDrift();
    const baseLandingDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const adjustedLandingDistance = baseLandingDistance - currentDrift;

    const speedConfig = this._getSpeedConfig();
    const overshoot = speedConfig.BOUNCE_OVERSHOOT;

    // Landing duration: velocity-derived (scales automatically with speed/tension)
    // When tension is active, _tensionSpeedMultiplier reduces effectiveVelocity,
    // which naturally produces a longer, gentler landing — same bounce, same formula.
    // Fallback to LANDING_DURATION if explicitly set (TURBO/QUICK backwards compat)
    let stopDuration;
    const landingDuration = speedConfig.LANDING_DURATION || null;
    if (landingDuration) {
      // Legacy path: hardcoded landing duration (TURBO/QUICK)
      stopDuration = landingDuration;
    } else {
      // Velocity-matched: landing adapts to current spin speed (including tension deceleration)
      const effectiveVelocity = this.normalizedSpinSpeed
        * (this._tensionDecelerating ? this._tensionSpeedMultiplier : 1.0);
      const velocityMatchedDuration =
        ((overshoot + 3) * adjustedLandingDistance * 1000) /
        effectiveVelocity;
      stopDuration = velocityMatchedDuration * (speedConfig.STOP_SLOWDOWN_FACTOR ?? 1.0);
      // Tension landing: stretch duration to compensate for overshoot appearing faster against slow spin.
      // Per-speed override (speedConfig.TENSION_LANDING_SLOWDOWN) takes priority over global config.
      // Turbo/super_turbo set 1.0 (no slowdown) since their decelerated velocity is still fast enough.
      if (this._tensionDecelerating) {
        const tensionLandingSlowdown = speedConfig.TENSION_LANDING_SLOWDOWN
          ?? FEATURES_CONFIG.TENSION.REEL_DECELERATION.TENSION_LANDING_SLOWDOWN
          ?? 1.0;
        stopDuration *= tensionLandingSlowdown;
      }

    }

    this._setLandingSymbolOnVisibleSprites();

    // Landing symbols are above the visible area — set them to normal (non-blurred)
    // textures immediately so they're already sharp when they scroll into view
    {
      const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
      for (let i = 0; i < landingZoneEnd; i++) {
        const spriteData = this.spinningSprites[i];
        if (!spriteData) continue;
        spriteData.normalSprite.alpha = 1;
        if (spriteData.blurredSprite) spriteData.blurredSprite.alpha = 0;
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }

      this.isBlurred = false;
    }

    if (this.reels && this.reels.length > 1) {
      this.symbolId = this.reels[1];
    }

    // Store stop params for the ticker to use (replaces anime.js tween)
    this._stopElapsed = 0;
    this._stopDistance = adjustedLandingDistance;
    this._stopDuration = stopDuration;
    this._stopOvershoot = overshoot;
    this._stopLastEasedPos = 0;



    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = adjustedLandingDistance;
    this._landingPeakFired = false; // Reset peak detection for this landing
    this.isPlaying = false;
    this.isDecelerating = false;
  }

  // Shared landing finalization — called when ticker-based stop animation completes
  _finalizeLanding() {
    // Remove ticker — stop animation is done
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Clear stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;
    this.stopAnimationProgress = 0;
    this.isDecelerating = false;

    // Reset tension deceleration state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    const hadPendingResize = this._pendingPositionUpdate;
    if (this._pendingPositionUpdate) {
      const wasStopped = this.hasStopped;
      const wasStopTweenActive = this.stopTweenActive;
      this.stopTweenActive = false;
      this.activeStopAnimation = null;

      this._applyPendingResize();

      this.hasStopped = wasStopped;
      this.stopTweenActive = wasStopTweenActive;
    }

    // Rotate array: move last N sprites to front (single O(n) operation)
    const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE_SYMBOLS;
    const tail = this.spinningSprites.splice(cut);
    this.spinningSprites.unshift(...tail);

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Set critical state flags immediately
    this.hasStopped = true;
    this.stopTweenActive = false;
    // Bookkeeping only — the loop above already placed every sprite
    // absolutely, so a delta shift here would move them a second time.
    this.reelYOffset = 0;
    this.lockedSpinSpeed = null;
    this.currentVelocity = 0;

    // 🚀 FIRE LANDING ANIMATIONS IMMEDIATELY - highest priority for smoothest UX
    if (this.onReelStop) {
      this.onReelStop(this.reelIndex, this.reels);
    }

    // Fire onReelLanded on the NEXT frame — gives landing animations an uncontested
    // render frame before tension operations (dim, sound, spine) kick in
    if (this.onReelLanded) {
      const callback = this.onReelLanded;
      const reelIndex = this.reelIndex;
      requestAnimationFrame(() => callback(reelIndex));
    }

    // Heavy operations AFTER landing animations start (non-blocking for animations)
    if (hadPendingResize) {
      this._forceRescaleAllTextures();
    }

    // Unblur all remaining sprites (bottom buffer etc.)
    this._switchToNormalTextures();

    this._hideBufferSprites();

    // Reel stop sound + special symbol sounds
    // reel_stop already played at peak if overshoot was present; only play special sounds here.
    const isTurboMode = isTurboLike(this.autoPlaySpeed);
    const isTurboSimultaneousLanding = isTurboMode && !this.isTensionMode;
    const isManualStop = this._wasManualStopped;
    if (isTurboSimultaneousLanding) {
      // ── reel_stop, because `quick_stop` has no file ──
      //
      // This used to play `quick_stop`, a name with no entry in sounds.js and
      // no mp3 behind it — so `play()` silently no-opped and a slammed or
      // turbo-landed board arrived in COMPLETE SILENCE unless it happened to
      // hold a scatter or a mystery. The `thud: false` below is written on the
      // assumption that this line already made the landing's noise; it did
      // not, so the board made none at all.
      //
      // `reel_stop` is that noise, and the guards around this line already
      // make it once for the whole board rather than once per reel — which is
      // the whole reason a separate quick_stop was wanted.
      // Turbo/super_turbo: ONCE on the first reel — all land simultaneously.
      if (this.reelIndex === 0) {
        soundManager.play(SOUND_NAME.reel_stop);
      }
    } else if (isManualStop) {
      // Manual stop: once, on the first reel that was force-stopped.
      if (!ReelControllerV2._manualStopSoundPlayed) {
        ReelControllerV2._manualStopSoundPlayed = true;
        soundManager.play(SOUND_NAME.reel_stop);
      }
    } else if (!this._landingPeakFired) {
      // A landing with NO overshoot never reaches the peak, so its sound is
      // owed here instead. Thud or announcement, same rule.
      this._playLandingSound();
    }

    // ── A MANUAL stop still owes the whole announcement ──
    //
    // It skips the peak, and `quick_stop` above is its landing sound — so only
    // the scatter/mystery half is added, never a second thud. Without this,
    // slamming the spin silenced scatter_land and mystery_land completely: the
    // symbol arrived and said nothing, and all that was left was `quick_stop`,
    // which has no file.
    if (isManualStop) {
      this._playLandingSound({ thud: false });
    }

    // ── TURBO speaks for the mystery, and for nothing else ──
    //
    // Turbo is a promise to get out of the way: no landing animations, no
    // per-symbol announcements, `quick_stop` once for the whole board. A
    // scatter landing keeps quiet here for the same reason its bounce does.
    //
    // The mystery is the one exception, because it does not keep the promise:
    // its reveal STOPS the board and plays a build-up and a set of doors
    // whatever the speed. Something that halts the game to perform cannot do
    // it in silence, so it gets its sound even here.
    //
    // Asked with _hasLandedMystery rather than _playLandingSound: the resolver
    // returns one sound by priority, and a reel holding a scatter AND a
    // mystery would answer `scatter_land` — which turbo then drops, taking the
    // mystery down with it.
    if (isTurboSimultaneousLanding && this._hasLandedMystery()) {
      soundManager.playOncePerFrame(SOUND_NAME.mystery_land);
    }

    if (this.reelIndex === Settings.REEL_COUNT - 1) {
      soundManager.stop(SOUND_NAME.reel_spin);
      this._completeSpin();
    }
  }

  /**
   * The spin is over — but only once the board has stopped moving.
   *
   * ── Why the landing is part of the spin ──
   *
   * `setSpinComplete` does not just mark completion: its reducer also clears
   * `isSpinning`, and that one flag is what reopens the spin button, releases
   * the win presentation and lets autoplay fire again. Dispatched the instant
   * the last reel stopped, all three were told the round was over while the
   * landing bounces were still in the air — so a manual press started the next
   * spin on top of them, and the next spin clears track 0, the very track the
   * bounce is playing on. Always the last reel, because the reels stop 200ms
   * apart and only the final one is still moving.
   *
   * Autoplay and the win path each grew their own wait for this. Holding the
   * SIGNAL instead means there is one answer to "is the round finished", and
   * every consumer gets it for free — the spin button needed no change at all.
   *
   * `onReelStop` starts the landings and runs immediately before this, so they
   * are already registered by the time it is asked.
   *
   * Costs nothing when there is nothing to wait for: turbo, super turbo and
   * every stop path that suppresses landings register none, so the wait
   * resolves on the next microtask. The helper carries its own stall guard, so
   * a spine that dies mid-clip cannot hold the round.
   */
  _completeSpin() {
    const wait = this.waitForBoardSettle;
    if (typeof wait !== "function") {
      this.dispatch(setSpinComplete(true));
      return;
    }
    wait().then(() => {
      this.dispatch(setSpinComplete(true));
    });
  }

  // Forces re-scaling of all sprite textures
  _forceRescaleAllTextures() {
    if (!this.spinningSprites.length) return;

    this.spinningSprites.forEach((spriteData) => {
      if (spriteData.currentSymbolId !== null) {
        const info = symbolData[spriteData.currentSymbolId];
        if (info?.name) {
          const scale = this._calculateSymbolScale(
            info.name,
            this.symbolWidth,
            this.symbolHeight,
            spriteData.currentSymbolId,
          );
          if (spriteData.normalSprite) {
            spriteData.normalSprite.scale.set(scale);
          }
          if (spriteData.blurredSprite) {
            spriteData.blurredSprite.scale.set(scale);
          }
        }
      }
    });
  }

  // Set landing symbol - typically called with API data during spin
  setLandingSymbol(symbolId) {
    if (symbolId === undefined || symbolId === null) {
      return;
    }

    if (this.isPlaying && this.spinningSprites.length > 0) {
      if (this._isLandingSpriteVisible()) {
        this.pendingLandingSymbolId = symbolId;
        this.symbolId = symbolId;
      } else {
        this.symbolId = symbolId;
        this.pendingLandingSymbolId = null;
        this._setLandingSymbolOnVisibleSprites(symbolId);
      }
      return;
    }

    this.symbolId = symbolId;
    this.updateSymbolTexture(symbolId);
  }

  // Applies pending resize
  _applyPendingResize() {
    if (!this._pendingPositionUpdate) return;

    const oldSymbolHeight = this._originalSymbolHeight || this.symbolHeight;
    const newSymbolHeight = this._targetSymbolHeight;
    // Second line of defence. Guard 1 in resizeSymbol keeps a degenerate size
    // out, but this is the multiplication that does the damage, so it refuses
    // anything it cannot safely scale by — 0, Infinity and NaN all land here.
    // Falling through to the absolute branch repositions from `i * symbolHeight`
    // instead, which is always valid.
    const rawRatio = newSymbolHeight / oldSymbolHeight;
    const ratioUsable = Number.isFinite(rawRatio) && rawRatio > 0;
    const scaleRatio = ratioUsable ? rawRatio : 1;
    // ── `isPlaying` is already FALSE during the landing ──
    //
    // It is cleared the moment the stop begins (see stopSpin), so testing it
    // alone sent a mid-landing resize down the ABSOLUTE branch below, which
    // repositions every sprite to `i * symbolHeight` and snaps the reel out of
    // its bounce. The strip is still being translated during the landing
    // exactly as it is during the spin, so it needs the same proportional
    // treatment — hence stopTweenActive here.
    const isSpinning =
      (this.isPlaying || this.stopTweenActive) && !this.hasStopped;
    // Only the TURBO drop, which is anime-driven: its remaining travel lives in
    // closure variables this cannot reach, so it is still deferred to the end.
    // The normal landing is the ticker path below and keeps its travel on
    // `this`, which is what makes it rescalable at all.
    const isStopAnimating = this.stopTweenActive && this.activeStopAnimation;

    if (isStopAnimating) {
      return;
    }

    this.symbolHeight = this._targetSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;
    this.nativeDimensions = null;

    // Invalidate caches when symbol height changes
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache.clear(); // Clear symbol scale cache

    // ── The landing, re-expressed in the new height ──
    //
    // The ticker stop is `easedPosition = _stopDistance * easedT`, with the
    // delta taken against `_stopLastEasedPos`. Both are PIXELS derived from the
    // old symbol height. Because the easing is a linear function of
    // `_stopDistance`, scaling the distance, the last position and the sprite
    // positions by one ratio re-expresses the whole tween in the new height
    // EXACTLY — same curve, same remaining time, same landing row. Nothing is
    // approximated and the reel does not jump.
    //
    // Scale before the positions below, so every number crosses together.
    if (
      ratioUsable &&
      this.stopTweenActive &&
      this._stopDuration > 0 &&
      Math.abs(scaleRatio - 1.0) > 0.0001
    ) {
      this._stopDistance *= scaleRatio;
      this._stopLastEasedPos *= scaleRatio;
      // Progress reporting reads this; left unscaled it would disagree with
      // _stopDistance and report a landing as further along than it is.
      this.stopAnimationTotalDistance *= scaleRatio;
    }


    if (ratioUsable && isSpinning && Math.abs(scaleRatio - 1.0) > 0.0001) {
      this.spinningSprites.forEach((spriteData) => {
        spriteData.container.y =
          Math.round(spriteData.container.y * scaleRatio * PRECISION_FACTOR) /
          PRECISION_FACTOR;
      });

      this.lastCalculatedPosition =
        Math.round(
          this.lastCalculatedPosition * scaleRatio * PRECISION_FACTOR,
        ) / PRECISION_FACTOR;

      const speedConfig = this._getSpeedConfig();
      this.normalizedSpinSpeed =
        (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    } else {
      this.spinningSprites.forEach((spriteData, i) => {
        spriteData.container.y = i * this.symbolHeight;
      });
    }

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData) => {
        if (spriteData.currentSymbolId !== null) {
          const info = symbolData[spriteData.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight,
              spriteData.currentSymbolId,
            );
            if (spriteData.normalSprite) {
              spriteData.normalSprite.scale.set(scale);
            }
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.scale.set(scale);
            }
          }
        }
        // Update font size for coin value text (both normal and blurred)
        if (spriteData.valueText?._label !== undefined) {
          // The coin label sprite (coinLabel): same number, new size —
          // the new size's labels pre-drawn first (keyed, once).
          this._warmCoinLabels();
          setCoinLabel(
            spriteData.valueText,
            spriteData.valueText._label,
            this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER,
            // this.symbolWidth * 0.76,
            this.symbolHeight * COIN_LABEL_MAX_WIDTH_RATIO, // the spine's ratio
          );
        } else if (spriteData.valueText) {
          const displayValue = spriteData.valueText.text;
          const fontSize =
            this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          spriteData.valueText.fontSize = fontSize;

          // Reset scale first
          spriteData.valueText.scale.set(1);

          // Force update to get accurate measurements with new font size
          spriteData.valueText.updateText();
          spriteData.valueText.dirty = true;

          // Recalculate scaling with new dimensions (if text has content)
          if (displayValue !== "") {
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = spriteData.valueText.width;

            if (actualTextWidth > maxTextWidth) {
              const digitCount = displayValue.replace(/[^0-9]/g, "").length;

              if (digitCount <= 4) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else if (digitCount >= 5 && digitCount <= 8) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else {
                const scale = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.set(scale);
              }
            }
          }

          // Update blurred text with same logic
          if (spriteData.valueTextBlurred) {
            spriteData.valueTextBlurred.fontSize = fontSize;

            // Reset scale first
            spriteData.valueTextBlurred.scale.set(1);

            // Force update to get accurate measurements with new font size
            spriteData.valueTextBlurred.updateText();
            spriteData.valueTextBlurred.dirty = true;

            // Recalculate scaling with new dimensions (if text has content)
            if (displayValue !== "") {
              const coinWidth = this.symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = spriteData.valueTextBlurred.width;

              if (actualTextWidth > maxTextWidth) {
                const digitCount = displayValue.replace(/[^0-9]/g, "").length;

                if (digitCount <= 4) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else if (digitCount >= 5 && digitCount <= 8) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else {
                  const scale = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.set(scale);
                }
              }
            }
          }
        }
      });
    }

    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = this.symbolHeight;
  }

  // Update dimensions when symbol size changes
  resizeSymbol(newSymbolWidth, newSymbolHeight) {
    // ── A 0x0 layout pass must never reach the geometry ──
    //
    // The layout emits a degenerate size while a device/orientation change is
    // in flight. Taken at face value it is fatal, and PERMANENTLY so, because
    // every position here is ratio-based:
    //
    //   159 -> 0    ratio 0         every container.y collapses to 0
    //   0   -> 38   ratio Infinity  every container.y becomes Infinity/NaN
    //
    // A NaN position never recovers, so the reel stops landing, never reaches
    // _completeSpin, and the round hangs with the game unplayable. Captured on
    // a desktop -> mobile resize mid-spin: reels 0 and 1 landed, 2-4 never did.
    //
    // Dropped OUTRIGHT rather than deferred: there is nothing to remember. A
    // real size always follows, and resizeSymbol is called again with it.
    if (
      !Number.isFinite(newSymbolHeight) ||
      !Number.isFinite(newSymbolWidth) ||
      newSymbolHeight <= 0 ||
      newSymbolWidth <= 0
    ) {
      return;
    }

    this._targetSymbolHeight = newSymbolHeight;
    this._targetSymbolWidth = newSymbolWidth;

    if (
      this.symbolHeight === newSymbolHeight &&
      this.symbolWidth === this._targetSymbolWidth
    ) {
      this._pendingPositionUpdate = false;
      return;
    }

    const oldSymbolHeight = this.symbolHeight;


    // If spinning OR stopping, defer update to avoid mid-animation position chaos
    if ((this.isPlaying || this.stopTweenActive) && !this.hasStopped) {
      this._originalSymbolHeight = oldSymbolHeight;
      this._pendingPositionUpdate = true;

      // ── Free-spinning: correct NOW, not at the next wrap ──
      //
      // The visible window starts at sprite index 7, and every sprite sits at
      // `i * symbolHeight`. So a stale height does not merely mis-space the
      // strip — it offsets the whole visible band by 7 x (new - old), which is
      // the large empty gap under the reels after a mid-spin resize.
      //
      // The correction already existed and is already proven: the `isSpinning`
      // branch of _applyPendingResize scales every position by the height
      // ratio, which is exact for a strip that is only ever translated and
      // wrapped. It was simply not reached until the next sprite WRAPPED
      // (see the wrap loop), so the gap stayed on screen until then — and on a
      // slow spin or during the wind-up, that is a long time.
      //
      // Nothing new is computed here; the same call is just made sooner.
      //
      // The STOP tween is deliberately left out. Its remaining travel is held
      // in old-height units inside the tween, so rescaling underneath it would
      // land the reel offset — the end of the stop repositions absolutely and
      // would snap. That window is a separate problem and a far riskier one.
      // The anime-driven TURBO drop is the one case still deferred — see
      // isStopAnimating in _applyPendingResize for why it cannot be rescaled.
      if (!this.activeStopAnimation) {
        this._applyPendingResize();
      }
      return;
    }

    // Not animating: update immediately
    this.symbolHeight = newSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;
    this._originalSymbolHeight = oldSymbolHeight;
    this._pendingPositionUpdate = false;
    this.nativeDimensions = null;

    // Invalidate caches when symbol height changes
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache.clear(); // Clear symbol scale cache

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData, i) => {
        spriteData.container.y = i * this.symbolHeight;
      });
    }

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData) => {
        if (spriteData.currentSymbolId !== null) {
          const info = symbolData[spriteData.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight,
              spriteData.currentSymbolId,
            );
            if (spriteData.normalSprite) {
              spriteData.normalSprite.scale.set(scale);
            }
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.scale.set(scale);
            }
          }
        }
        // Update font size and position for coin value text (both normal and blurred)
        if (spriteData.valueText?._label !== undefined) {
          // The coin label sprite (coinLabel): same number, new size —
          // the new size's labels pre-drawn first (keyed, once).
          this._warmCoinLabels();
          setCoinLabel(
            spriteData.valueText,
            spriteData.valueText._label,
            this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER,
            // this.symbolWidth * 0.76,
            this.symbolHeight * COIN_LABEL_MAX_WIDTH_RATIO, // the spine's ratio
          );
        } else if (spriteData.valueText) {
          const displayValue = spriteData.valueText.text;
          const fontSize =
            this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          spriteData.valueText.fontSize = fontSize;
          spriteData.valueText.y =
        -COIN_VALUE_Y_OFFSET * (this.symbolHeight / Settings.SYMBOL_HEIGHT);

          // Reset scale first
          spriteData.valueText.scale.set(1);

          // Force update to get accurate measurements with new font size
          spriteData.valueText.updateText();
          spriteData.valueText.dirty = true;

          // Recalculate uniform scaling if text overflows
          if (displayValue !== "") {
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = spriteData.valueText.width;

            if (actualTextWidth > maxTextWidth) {
              const scale = maxTextWidth / actualTextWidth;
              spriteData.valueText.scale.set(scale);
            }
          }

          // Update blurred text with same logic
          if (spriteData.valueTextBlurred) {
            spriteData.valueTextBlurred.fontSize = fontSize;
            spriteData.valueTextBlurred.y =
          -COIN_VALUE_Y_OFFSET * (this.symbolHeight / Settings.SYMBOL_HEIGHT);

            spriteData.valueTextBlurred.scale.set(1);
            spriteData.valueTextBlurred.updateText();
            spriteData.valueTextBlurred.dirty = true;

            if (displayValue !== "") {
              const coinWidth = this.symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = spriteData.valueTextBlurred.width;

              if (actualTextWidth > maxTextWidth) {
                const scale = maxTextWidth / actualTextWidth;
                spriteData.valueTextBlurred.scale.set(scale);
              }
            }
          }
        }
      });
    }
  }

  // Buffer Sprite Visibility — hide non-visible sprites when idle to prevent overflow bleed

  // Hides buffer sprites (above/below visible area) when reels are idle
  _hideBufferSprites() {
    if (!FEATURES_CONFIG.SPIN.HIDE_IDLE_BUFFERS) return;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const outsideWindow =
        i < VISIBLE_SYMBOL_START_INDEX ||
        i >= VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT;
      // The window sprites are shown EXPLICITLY, not just left alone: the
      // per-frame cull below may have hidden any of them while the strip was
      // moving, and this is the settled state, so it has to be complete.
      this.spinningSprites[i].container.visible = !outsideWindow;
    }
  }

  /**
   * Hide the sprites that are outside the mask THIS FRAME.
   *
   * At rest the strip has a fixed set of buffers and _hideBufferSprites deals
   * with them. While it moves there is no such set: every sprite takes its turn
   * inside the window, so "buffer" means "outside the window right now" and the
   * answer changes every frame.
   *
   * Without this, a spin submits all SPRITE_COUNT sprites per reel and the
   * mask's stencil test throws about half of them away. The strip has to be
   * SYMBOLS_VISIBLE * 2 + 2 cells long because the landing stages a whole board
   * above the window; the window itself never holds more than
   * SYMBOLS_VISIBLE + 1.
   *
   * One comparison per sprite, inside loops that are already touching every
   * sprite to move it. It moves nothing and touches no texture — the strip runs
   * exactly as before, it is just drawn less.
   */
  _cullOffscreenSprites() {
    // ── Gated on the SAME flag as _hideBufferSprites ──
    //
    // That method and _showBufferSprites are the only things that put
    // `visible` back to true, and both early-return when HIDE_IDLE_BUFFERS is
    // off. Culling without the same guard would hide sprites mid-spin that
    // nothing would ever show again, and half the strip would settle blank.
    // One flag owns whether this class touches `visible` at all.
    if (!FEATURES_CONFIG.SPIN.HIDE_IDLE_BUFFERS) return;

    const h = this.symbolHeight;
    if (!h) return;

    // The window, in SPRITE-LOCAL coordinates. At rest the visible cells are
    // VISIBLE_SYMBOL_START_INDEX .. + VISIBLE_SYMBOL_COUNT sitting at y = i * h,
    // and a cell reaches half a height either side of its own y — so a sprite
    // overlaps the window while its y is within one cell of that range. The
    // margin covers ReelSetMask's padding (MASK_PADDING_MULTIPLIER, 0.02 of a
    // cell) several times over.
    const margin = h * 0.1;
    const top = (VISIBLE_SYMBOL_START_INDEX - 1) * h - margin;
    const bottom =
      (VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT) * h + margin;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const container = this.spinningSprites[i]?.container;
      if (!container) continue;
      const onScreen = container.y > top && container.y < bottom;
      if (container.visible !== onScreen) container.visible = onScreen;
    }
  }

  // Shows all buffer sprites before spin starts (restores full strip for animation)
  _showBufferSprites() {
    if (!FEATURES_CONFIG.SPIN.HIDE_IDLE_BUFFERS) return;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.visible = true;
    }
  }

  // Texture Visibility Control (for overlay spine animations)

  // Hides symbol texture to show overlay spine animation
  hideSymbolTexture(symbolIndex) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // ── 0, not 0.001 ──
    //
    // This is a deliberate hide: an overlay spine has taken the cell over and
    // the flat texture must not show through it. 0.001 still renders, so a win
    // across a full board left up to SYMBOLS_VISIBLE x REEL_COUNT sprites being
    // submitted for nothing; at 0 PIXI skips them. showSymbolTexture puts them
    // back at 1, and the spin loop's own crossfade is untouched — that one uses
    // 0.001 on purpose, to keep both layers live while they cross.
    if (sprite.normalSprite) {
      sprite.normalSprite.alpha = 0;
    }
    if (sprite.blurredSprite) {
      sprite.blurredSprite.alpha = 0;
    }
    if (sprite.valueText) {
      sprite.valueText.alpha = 0;
    }
    if (sprite.valueTextBlurred) {
      sprite.valueTextBlurred.alpha = 0;
    }
  }

  // Shows symbol texture (hides overlay animation)
  showSymbolTexture(symbolIndex) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // A COMPOSITE is not its symbol. `currentSymbolId` still says "gamble
    // scatter" — the closed door — and rescaling to that is what undid the
    // size set when the picture was applied. See _applyCompositeScale.
    if (sprite._staticOverride) {
      this._applyCompositeScale(sprite);
    } else if (sprite.currentSymbolId !== null) {
      const info = symbolData[sprite.currentSymbolId];
      if (info?.name) {
        const scale = this._calculateSymbolScale(
          info.name,
          this.symbolWidth,
          this.symbolHeight,
          sprite.currentSymbolId,
        );
        if (sprite.normalSprite) {
          sprite.normalSprite.scale.set(scale);
        }
        if (sprite.blurredSprite) {
          sprite.blurredSprite.scale.set(scale);
        }
      }
    }

    if (sprite.normalSprite) {
      sprite.normalSprite.alpha = 1;
    }
    if (sprite.blurredSprite) {
      sprite.blurredSprite.alpha = 0;
    }
    if (sprite.valueText && sprite.valueText.text !== "") {
      sprite.valueText.alpha = 1;
    }
    if (sprite.valueTextBlurred) {
      sprite.valueTextBlurred.alpha = 0;
    }
  }

  // Shows all visible symbol textures (clears overlay animations)
  showAllSymbolTextures() {
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;

    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (sprite) {
        // Recalculate scale when showing sprite (fixes scaling after expanding wild animations)
        if (sprite.currentSymbolId !== null) {
          const info = symbolData[sprite.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight,
              sprite.currentSymbolId,
            );
            if (sprite.normalSprite) {
              sprite.normalSprite.scale.set(scale);
            }
            if (sprite.blurredSprite) {
              sprite.blurredSprite.scale.set(scale);
            }
          }
        }

        if (sprite.normalSprite) {
          sprite.normalSprite.alpha = 1;
        }
        if (sprite.blurredSprite) {
          sprite.blurredSprite.alpha = 0;
        }
        if (sprite.valueText && sprite.valueText.text !== "") {
          sprite.valueText.alpha = 1;
        }
        if (sprite.valueTextBlurred) {
          sprite.valueTextBlurred.alpha = 0;
        }
      }
    }

    this._restoreStaticOverrides();
  }

  // Exposes sprites in format compatible with useWinController - returns only visible sprites
  get symbolsContainer() {
    const visibleStart = VISIBLE_SYMBOL_START_INDEX;
    const visibleEnd = visibleStart + VISIBLE_SYMBOL_COUNT;
    const visibleSprites = this.spinningSprites.slice(visibleStart, visibleEnd);

    return {
      children: visibleSprites.map((sprite, overlayIndex) => ({
        currentSymbolId: sprite.currentSymbolId,
        symbolId: sprite.currentSymbolId,
        overlayIndex: overlayIndex, // Direct mapping: 0, 1, 2, 3
        // The cell shows a SETTLED picture (see setSymbolTextureOnly), not the
        // symbol its id names — so nothing may play that symbol's spine on it.
        hasStaticOverride: !!sprite._staticOverride,
      })),
    };
  }

  // Expanding Wild Support

  // Replaces all visible symbols with target symbol (e.g., wild expansion)
  // @param {number} targetSymbolId - Symbol ID to replace with
  // @param {boolean} hideTextures - If true, hides textures (for cascade mode). If false, keeps visible (for full-reel mode)
  replaceVisibleSymbols(targetSymbolId, hideTextures = true) {
    const symbolInfo = symbolData[targetSymbolId];
    if (!symbolInfo?.texture) return;

    const textureName = symbolInfo.texture;
    const texture = this.spritesheet?.textures?.[`${textureName}.png`];
    if (!texture) return;

    // Replace all visible sprites
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;

    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (!sprite) continue;

      sprite.normalSprite.texture = texture;
      sprite.currentSymbolId = targetSymbolId;
      this._applySpriteZIndex(sprite, targetSymbolId);

      // Only hide textures if requested (cascade mode needs this, full-reel mode doesn't)
      if (hideTextures) {
        sprite.normalSprite.alpha = 0.001;
        sprite.blurredSprite.alpha = 0;
      }
    }
  }

  // Replaces single symbol at position (for cascading wild animation)
  replaceSymbolAtPosition(symbolIndex, targetSymbolId) {
    const symbolInfo = symbolData[targetSymbolId];
    if (!symbolInfo?.texture) return;

    // CRITICAL: Update the reels array for this position
    // This prevents blinking when next spin starts
    // Create a new array since the original might be read-only (from Redux state)
    if (this.reels && this.reels.length > symbolIndex) {
      this.reels = this.reels.map((id, idx) =>
        idx === symbolIndex ? targetSymbolId : id,
      );
    }

    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // Force update by temporarily clearing currentSymbolId to bypass early exit in _setSpriteTexture
    sprite.currentSymbolId = null;

    // Use _setSpriteTexture to properly update ALL textures (normal, blurred, coin values)
    // This ensures when showSymbolTexture() is called later, it reveals the correct wild texture
    this._setSpriteTexture(sprite, targetSymbolId);

    // Then hide the textures for the animation overlay to show
    sprite.normalSprite.alpha = 0.001;
    sprite.blurredSprite.alpha = 0;
  }

  /**
   * Swap ONLY the picture at a position. The cell keeps its identity.
   *
   * `replaceSymbolAtPosition` also rewrites `currentSymbolId` and `this.reels`,
   * because the expanding wild needs the cell to BECOME a wild —
   * useWinController reads `sprite.currentSymbolId` to choose which overlay
   * spine celebrates ("read from spinningSprites, which has wild replacements").
   *
   * A reveal outcome is the opposite case. The gamble scatter is still the
   * gamble scatter: all three scatters must celebrate together on the `scg`
   * spine, and only the STATIC texture underneath becomes the blank frame.
   * Rewriting the id here pointed the win lookup at a texture-only symbol with
   * no spine, so `syncWinAnimation` early-returned, the scatter never played
   * `win`, and it drifted against the other two (its leftover clip loops at
   * 0.367-0.500s against their 0.667s `win`).
   *
   * Alpha, zIndex and scale are deliberately NOT touched — the replacement
   * inherits whatever the texture it replaces already had, so a hidden cell
   * stays hidden and nothing changes layer or size.
   */
  setSymbolTextureOnly(symbolIndex, textureSymbolId, textureOverride = null) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // ── A ready-made picture wins ──
    //
    // The gamble scatter's settled cell is a COMPOSITE — the open gold window
    // with the card, its lit pips and its spin count already inside it, all
    // captured from the same objects the live reveal puts on screen. There is
    // no symbolData id for it and, more to the point, no label: the number is
    // baked in by the skeleton that owns it, so nothing has to reproduce a
    // bone chain out here any more.
    //
    // Everything else still comes off symbolData, unchanged.
    if (textureOverride) {
      // ── Recorded, because the board is torn down and rebuilt under it ──
      //
      // This swap is TEXTURE-ONLY on purpose: the cell's identity stays the
      // gamble scatter so the win cycle keeps celebrating it on `scg`. The
      // price is that every path which repaints a cell from its identity —
      // and the bonus transition's snapClearAll is one — puts the CLOSED gold
      // door back over a window the player watched open. That is what was
      // waiting for them when they came back from the bonus.
      //
      // So the override outlives the repaint. Cleared on the next spin start,
      // which is the only moment the cell legitimately becomes something else.
      sprite._staticOverride = textureOverride;
      if (sprite.normalSprite) sprite.normalSprite.texture = textureOverride;
      // The settled cell is at rest. Its blurred twin is only ever seen for
      // the frames between a spin starting and the strip taking over, so the
      // sharp picture is a better answer than a missing one.
      if (this.spritesheetBlurred && sprite.blurredSprite) {
        sprite.blurredSprite.texture = textureOverride;
      }
      // ── And SIZED, which this never did ──
      //
      // Swapping `.texture` leaves `scale` alone, so the composite was drawn
      // at whatever scale the sprite was last given — the scale of a spin
      // strip's symbol, or of the cell size the reel was built at before it
      // was resized. On the reload path that is measured before the board has
      // its final geometry, and nothing rescales it afterwards, so the card
      // inside the window came back a different size from the one that lands
      // live. Vladimir, 2026-09-23.
      this._applyCompositeScale(sprite);
      return;
    }

    const info = symbolData[textureSymbolId];
    if (!info?.texture) return;

    const texture = this.spritesheet?.textures?.[`${info.texture}.png`];
    if (!texture) return;

    sprite._staticOverride = texture;
    if (sprite.normalSprite) sprite.normalSprite.texture = texture;
    if (this.spritesheetBlurred && sprite.blurredSprite) {
      sprite.blurredSprite.texture =
        this.spritesheetBlurred.textures?.[`${info.texture}_blurred.png`] ||
        PIXI.Texture.EMPTY;
    }
  }

  /**
   * Put back any texture-only override a repaint has just undone.
   *
   * Called from showAllSymbolTextures, which is what the bonus transition's
   * snapClearAll ends with. See setSymbolTextureOnly for why the override has
   * to survive that at all.
   */
  _restoreStaticOverrides() {
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;
    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      const override = sprite?._staticOverride;
      if (!override) continue;
      if (sprite.normalSprite) sprite.normalSprite.texture = override;
      if (this.spritesheetBlurred && sprite.blurredSprite) {
        sprite.blurredSprite.texture = override;
      }
      // The repaint this is undoing rescaled the sprite to its symbol id.
      this._applyCompositeScale(sprite);
    }
  }


  // Cleanup

  clearAllTimeouts() {
    // Stop and clear all anime.js animations
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this._reelYTweenTarget); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
    }

    if (this.activeStopAnimation) {
      this.activeStopAnimation.pause();
      anime.remove(this.activeStopAnimation); // Force-eject from anime.js internal tracker
      this.activeStopAnimation = null;
    }

    // Remove PIXI.Ticker callback
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }
  }

  _cleanupSpinningSprites() {
    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((symbolData) => {
        try {
          if (symbolData.container?.parent && this.spinningContainer) {
            this.spinningContainer.removeChild(symbolData.container);
          }
          if (symbolData.normalSprite && !symbolData.normalSprite.destroyed) {
            symbolData.normalSprite.destroy();
          }
          if (symbolData.blurredSprite && !symbolData.blurredSprite.destroyed) {
            symbolData.blurredSprite.destroy();
          }
          if (symbolData.valueText && !symbolData.valueText.destroyed) {
            symbolData.valueText.destroy();
          }
          if (
            symbolData.valueTextBlurred &&
            !symbolData.valueTextBlurred.destroyed
          ) {
            symbolData.valueTextBlurred.destroy();
          }
          if (symbolData.container && !symbolData.container.destroyed) {
            symbolData.container.destroy();
          }
        } catch (e) {
          // ignore
        }
      });
      this.spinningSprites = [];
    }

    this.isPlaying = false;
    this.hasStopped = false;
  }
}

// Static flag: ensures quick_stop plays only once across all reels during manual stop
ReelControllerV2._manualStopSoundPlayed = false;

export default ReelControllerV2;
